import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/DatePicker.css';


import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Select,
    MenuItem,
    Chip,
    Button,
    IconButton,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    CircularProgress,
    Autocomplete,
    Checkbox,
    ListItemText,
    Menu,
    Grid,
    Stack,
    Divider,
    TableSortLabel,
    Popper,
    InputAdornment
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import {
    Assignment as AssignmentIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Clear as ClearIcon,
    Save as SaveIcon,
    LocationOn as LocationOnIcon,
    FileDownload as FileDownloadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    HelpOutline as HelpOutlineIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    UnfoldMore as UnfoldMoreIcon,
    ViewColumn as ViewColumnIcon,
    Notifications as NotificationsIcon,
    Check as CheckIcon,
    Settings as SettingsIcon,
    Send as SendIcon,
    Event as EventIcon,
    Engineering as EngineeringIcon,
    EventAvailable as EventAvailableIcon,
    EventBusy as EventBusyIcon,
    Description as DescriptionIcon,
    TableChart as TableChartIcon,
    Image as ImageIcon,
    ViewList as ViewListIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TableVirtuoso, GroupedTableVirtuoso } from 'react-virtuoso';
import { useDispatch, useSelector } from 'react-redux';
import { $authHost } from '../../http';
import { ktpIcon, pillarIcon, projectStatusOreolIcon } from '../layers/icons';
import '../../utils/arial-normal.js'
import '../../utils/arial-bold.js'
import { selectAccountingsGroupedByKtpNew, selectAccountingsGroupedByKtpNewWithoutAnyFilters, selectRulesMap, selectChecklistFilter, selectConclusionsForUser } from '../../store/selectors/supervisionSelector.js';
import { fetchAccounting, fetchConclusions, toggleDarkMode } from '../../store/reducers/stateReducer.js';
import Viewer from 'react-viewer';
import OnlyOfficeEditor from '../modules/DocumentServerNew.jsx';
import { createPortal } from 'react-dom';
import { AdvancedFiltersComponent } from './common/AdvancedFilters.jsx';
// Регистрируем русскую локаль
registerLocale('ru', ru);



function formatDateShort(str) {
    return formatDate(str, true)
}

function formatDate(str, short = false) {
    if (!str) return '';

    // Если дата в формате "YYYY-MM-DD" (из базы данных)
    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [, year, month, day] = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата уже в формате "DD-MM-YYYY"
    if (str.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [, day, month, year] = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата в формате "HH:MM DD-MM-YYYY"
    if (str.match(/^\d{2}:\d{2}\s\d{2}-\d{2}-\d{4}$/)) {
        const [, hours, minutes, day, month, year] = str.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        return `${hours}:${minutes} ${day}.${month}.${year}`;
    }

    // Попытка парсинга как обычной даты
    try {
        const date = new Date(str);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return short ? `${day}.${month}.${year}` : `${hours}:${minutes} ${day}.${month}.${year}`;
    } catch {
        return '';
    }
}

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        // Если дата уже в формате "YYYY-MM-DD" (из базы данных)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
        }

        // Если дата в формате "HH:MM DD-MM-YYYY"
        const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        if (dateTimeMatch) {
            const [, hours, minutes, day, month, year] = dateTimeMatch;
            return `${year}-${month}-${day}`;
        }

        // Если дата в формате "DD-MM-YYYY"  
        const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return `${year}-${month}-${day}`;
        }

        // Попытка парсинга как обычной даты
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

// Функция для форматирования даты из input в формат "YYYY-MM-DD" для базы данных
const formatDateFromInput = (inputValue) => {
    if (!inputValue) return '';
    try {
        const date = new Date(inputValue);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const columns = [
    {
        id: 'num_remarks', label: '№', minWidth: 50, width: 50,
        align: 'center',
    },
    {
        id: 'stage', label: 'Стадия', minWidth: 80, width: 80,
        align: 'center',
    },
    {
        id: 'complexity', label: 'Сложность', minWidth: 80, width: 80,
        align: 'center',
    },
    {
        id: 'task', label: 'Замечания', minWidth: 250, width: 300,
        align: 'center',
    },
    {
        id: 'status_remark', label: 'Статус замечания', minWidth: 100, width: 100,
        align: 'center',
    },
    {
        id: 'response_remark', label: 'Ответ на замечание', minWidth: 250, width: 300,
        align: 'center',
    },
    {
        id: 'result', label: 'Результат', minWidth: 250, width: 300,
        align: 'center',
    },
    {
        id: 'responsible', label: 'Исполнитель', minWidth: 150, width: 200,
        align: 'center',
    },
    {
        id: 'editor', label: 'Редактор', minWidth: 150, width: 200,
        align: 'center',
    },
    {
        id: 'creator', label: 'Создатель', minWidth: 150, width: 200,
        align: 'center',
    }
];

const VirtuosoTableComponents = {

    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            width: '100%',
            minWidth: '100%',
            // overflowX: 'auto', // для скролла
        }} />
    )),
    Table: (props) => (
        <Table {...props} sx={{
            borderCollapse: 'separate',
            tableLayout: 'auto', // Автоматическое распределение ширины
            bgcolor: 'transparent',
            width: '100%', // Используем всю доступную ширину
            minWidth: '100%', // Минимальная ширина 100%
        }} />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

function fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths) {

    // Функция для получения иконки сортировки
    const getSortIcon = (columnId) => {
        if (sortBy !== columnId) {
            return null
            // return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
        }
        return sortDirection === 'asc'
            ? <ArrowUpwardIcon fontSize="small" />
            : <ArrowDownwardIcon fontSize="small" />;
    };

    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    return (
        <>
            <TableRow>
                {visibleColumnsArray.map((column, index) => (
                    <TableCell
                        key={column.id}
                        variant="head"
                        align={column.align || ''}
                        style={{
                            width: columnWidths[column.id] || column.minWidth,
                            minWidth: column.minWidth,
                            maxWidth: columnWidths[column.id] || column.minWidth
                        }}
                        colSpan={column.colSpan}
                        sx={{
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderBottom: darkMode
                                ? '2px solid rgba(255, 255, 255, 0.12)'
                                : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRight: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            p: '8px',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            },
                            // Принудительно переопределяем цвета для всех дочерних элементов
                            '& .MuiTableSortLabel-root': {
                                color: darkMode ? '#f5f7fa' : '#333',
                            },
                            '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                color: darkMode ? '#64b5f6' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-root:hover': {
                                color: darkMode ? '#ffffff' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-icon': {
                                color: darkMode ? '#f5f7fa' : '#333',
                            },
                            '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                color: darkMode ? '#64b5f6' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon': {
                                color: darkMode ? '#ffffff' : '#1976d2',
                            },
                        }}
                        onClick={() => handleSort(column.id)}
                    >
                        <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                            sx={{
                                color: darkMode ? '#f5f7fa' : '#333',
                                '&:hover': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-active': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '& .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Принудительно переопределяем цвета для всех состояний
                                '&.MuiTableSortLabel-root': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-root:hover': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Стили для иконки во всех состояниях
                                '& .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '& .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Принудительно переопределяем все возможные цвета
                                '& *': {
                                    color: 'inherit !important',
                                },
                            }}

                        >

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {column.tooltip ? (
                                    <Tooltip title={column.tooltip}>
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                            {column.label}
                                        </Typography>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                        {column.label}
                                    </Typography>
                                )}
                                {/* {getSortIcon(column.id)} */}
                            </Box>

                        </TableSortLabel>
                    </TableCell>

                ))}
            </TableRow>
            {/* Второй ряд заголовков для подколонок дат */}
            {/* <TableRow>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        {[...Array(6)].map((_, index) => (
          <TableCell key={index} sx={{
            bgcolor: darkMode ? '#2b2b2b' : '#fff',
            borderBottom: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 57,
            zIndex: 10,
          }}></TableCell>
        ))}
      </TableRow> */}
        </>
    );
}

// Функция для отображения заголовка группы
function groupContent(index, groupedData, darkMode, user, visibleColumns, setImageViewerActiveIndex, setImageViewerVisible, setImages, setEditor) {
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return null;
    }

    const group = groupedData.groups[index]
    const groupName = group.name || `Группа ${index}`;
    const groupCount = groupedData.groupCounts[index];


    return (
        <td colSpan={columns.filter(col => visibleColumns[col.id]).reduce((a, v) => a + (v.colSpan || 1), 0)} style={{
            backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
            color: darkMode ? '#64b5f6' : '#1976d2',
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '6px 16px 3px 16px',
            borderBottom: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
            borderTop: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>


                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Chip с количеством объектов */}
                        <Chip
                            label={`${groupCount}`}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                            }}
                        />


                    </Box>


                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Кнопка создания объекта в группе */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                            {groupName}
                        </Typography>
                    </Box>




                </Box>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                    {/* Extended Table */}
                    <Table sx={{ marginTop: '0px' }}>
                        <TableHead>
                            <TableRow sx={{
                                borderBottom: 'none',
                                // borderBottom: darkMode
                                //     ? '1px solid rgba(255, 255, 255, 0.12)'
                                //     : '1px solid rgba(0, 0, 0, 0.12)',
                                borderTop: 'none',
                                '&:last-child td, &:last-child th': {
                                    border: 'none',
                                },
                            }}>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Заключение
                                </TableCell>
                                {group.attach && (
                                    <TableCell align="center" sx={{
                                        padding: '0 5px', fontWeight: 'normal',
                                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                                        color: darkMode ? 'white' : 'black'
                                    }}>
                                        Вложения
                                    </TableCell>
                                )}
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Готовность,%
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Отправитель
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Замечания / Повторные замечания
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    От даты
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Срок
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0 5px', fontWeight: 'normal',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    Статус
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{
                                borderBottom: 'none',
                                borderTop: 'none',
                                '&:last-child td, &:last-child th': {
                                    border: 'none',
                                },
                            }}>
                                <TableCell align="center" sx={{
                                    padding: '0px 8px',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    {group.name_conclusion}
                                </TableCell>
                                {group.attach && (
                                    <TableCell align="center" sx={{
                                        padding: '0px 8px',
                                        fontSize: { xs: '0.7rem', md: '0.8rem' }
                                    }}>
                                        {group.attach.map((att, attIndex) => (
                                            <IconButton
                                                key={attIndex}
                                                size="small"
                                                sx={{ padding: '0 2px', cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (['docx', 'xlsx', 'pdf'].includes(att.ext)) {
                                                        console.log(att)
                                                        // containerRef.current.scrollMem = containerRef.current.scrollTop;
                                                        // saveScrollPosition()
                                                        setEditor({
                                                            ...att,
                                                            file: `https://vio.komoks.ru/fls/${att.file}`
                                                        });
                                                    } else if (['jpg', 'png'].includes(att.ext)) {
                                                        const imageAttachments = group.attach.filter(a => ['jpg', 'png'].includes(a.ext));
                                                        setImages(imageAttachments.map(img => ({
                                                            src: `https://vio.komoks.ru/fls/${img.file}`,
                                                            alt: img.file,
                                                            downloadUrl: `https://vio.komoks.ru/fls/${img.file}`
                                                        })));
                                                        const clickedIndex = imageAttachments.findIndex(img => img.file === att.file);
                                                        setImageViewerActiveIndex(clickedIndex);
                                                        setImageViewerVisible(true);
                                                    } else {
                                                        window.open(`https://vio.komoks.ru/fls/${att.file}`);
                                                    }
                                                }}
                                            >
                                                {getFileIcon(att.ext)}
                                            </IconButton>
                                        ))}
                                    </TableCell>
                                )}
                                <TableCell align="center"
                                    sx={{
                                        padding: '0px 8px',
                                        color: `hsl(${group.proc === '0' ? '0' : (group.proc === '100' ? '120' : '57')}, 100%, 30%)`,
                                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {group.proc}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0px 8px',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    {group.sender}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0px 8px',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    color: darkMode ? 'white' : 'black'
                                }}>
                                    {groupCount} / {group.repeated_count}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0px 8px',
                                    color: '#3D3DB4',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                        <EventAvailableIcon sx={{ fontSize: '1.4rem' }} />
                                        {group.ext_date}
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    padding: '0px 8px',
                                    color: '#F6374C',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                        <EventBusyIcon sx={{ fontSize: '1.4rem' }} />
                                        {group.deadline}
                                    </Box>
                                </TableCell>
                                <TableCell align="center"
                                    sx={{
                                        padding: '0px 8px',
                                        color: statuses[group.status]?.stage_color || (darkMode ? 'white' : 'black'),
                                        fontSize: { xs: '0.7rem', md: '0.8rem' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                                        {getStatusIcon(statuses[group.status]?.stage_icon)}
                                        {statuses[group.status]?.stage}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>

            </Box>
        </td>
    );
}

const fieldColors = {
    name: { light: '#2e7d32', dark: '#4caf50' },
    dates: { light: '#1976d2', dark: '#64b5f6' },
    stage: { light: '#6a1b9a', dark: '#ab47bc' },
    responsible: { light: '#d32f2f', dark: '#f44336' },
    coexecutors: { light: '#f57c00', dark: '#ffb74d' },
    equipment: { light: '#3949ab', dark: '#5c6bc0' },
};

const EditableSelectCell = ({
    groupId,
    id,
    field,
    value,
    renderValue,
    changeValue,
    optionLabel,
    saveValue,
    equlity,
    label,
    saveEditedField,
    darkMode,
    options,
    multiple = false
}) => {
    console.log(id, field, value, label, darkMode, options, multiple)
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'responsible':
                return {
                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                    color: darkMode ? '#ffb74d' : '#CF6900',
                    selectedBg: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                };
            default:
                return {
                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                    color: darkMode ? '#ce93d8' : '#9c27b0',
                    selectedBg: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value || (multiple ? [] : null));
    const [inputValue, setInputValue] = useState('');

    const handleChange = (event, newValue) => {
        console.log(newValue)
        setLocalValue(changeValue ? changeValue(newValue) : newValue);
    };

    const handleInputChange = (event, newInputValue) => {
        console.log(newInputValue)
        setInputValue(newInputValue);
    };

    const handleBlur = () => {
        saveEditedField({
            groupId: groupId,
            rowId: id,
            field: field,
            value: saveValue ? saveValue(localValue) : localValue,
            isValueChanged: JSON.stringify(value) !== JSON.stringify(localValue)
        });
    };

    const handleClear = () => {
        setLocalValue(multiple ? [] : null);
    };

    const handleSave = () => {
        handleBlur();
    };

    const getOptionLabel = (option) => {
        return optionLabel ? optionLabel(option) : option?.name || option?.label || String(option || '');
    };

    const isOptionEqualToValue = (option, value) => {
        return equlity ? equlity(option, value) : option?.id === value?.id;
    };

    return (
        <Autocomplete
            value={localValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            options={options || []}
            getOptionLabel={getOptionLabel}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={isOptionEqualToValue}
            multiple={multiple}
            size="small"
            autoFocus
            disableClearable={true}
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            disableCloseOnSelect={multiple}
            renderInput={(params) => {
                console.log(params)
                return <TextField
                    {...params}
                    size="small"
                    placeholder={label}
                    sx={{
                        '& .MuiOutlinedInput-input': {

                            cursor: 'text',
                            color: darkMode ? '#ffffff' : '#333',
                            fontSize: '0.875rem',
                            width: 'fit-content !important',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColors.color,
                            borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                        },
                        '& .MuiInputLabel-root': {
                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                            fontSize: '0.875rem',
                            '&.Mui-focused': {
                                color: fieldColors.color,
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                            borderRadius: '4px',
                        },
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        '& .MuiInputBase-root': {
                            p: '5px !important',
                            borderRadius: '4px',
                        },
                        '& .MuiInputBase-input': {

                            fontWeight: 500,
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                        },
                    }}
                />
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Typography variant="body2">
                        {getOptionLabel(option)}
                    </Typography>
                </Box>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getOptionLabel(option)}
                        onClick={() => { }}
                        size="small"
                        sx={{
                            bgcolor: fieldColors.bgcolor,
                            color: fieldColors.color,
                            fontSize: '0.7rem',
                            height: 'auto',
                            minHeight: '20px',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                padding: '2px 6px',
                            },
                        }}
                    />
                ))
            }
            sx={{
                '& .MuiAutocomplete-popper': {
                    zIndex: 1300,
                },
                '& .MuiAutocomplete-paper': {
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                    '& .MuiAutocomplete-option': {
                        color: darkMode ? '#f5f7fa' : '#333',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                        '&[aria-selected="true"]': {
                            bgcolor: fieldColors.selectedBg,
                        },
                    },
                },
            }}
        />
    );
};

const EditableTextAreaCell = ({ groupId, id, field, value, label, saveEditedField, darkMode }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'response_remark':
                return {
                    color: darkMode ? '#2196f3' : '#1565c0',
                };
            case 'result':
                return {
                    color: darkMode ? '#4caf50' : '#2e7d32',
                };
            default:
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value);

    const handleTextChange = (e) => {
        setLocalValue(e.target.value);
    };

    const handleBlur = () => {
        saveEditedField({ groupId: groupId, rowId: id, field: field, value: localValue, isValueChanged: value !== localValue });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleBlur();
        }
    };

    return (
        <TextField
            multiline
            rows={3}
            size="small"
            value={localValue || ''}
            onChange={handleTextChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            sx={{
                '& .MuiOutlinedInput-input': {
                    p: '0px',
                    cursor: 'text',
                    color: darkMode ? '#ffffff' : '#333',
                    fontSize: '0.8rem',
                    lineHeight: '1.1',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldColors.color,
                    borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                    fontSize: '0.8rem',
                    '&.Mui-focused': {
                        color: fieldColors.color,
                    }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                },
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                '& .MuiInputBase-root': {
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    minWidth: '200px',
                    width: '100%'
                },
                '& .MuiInputBase-input': {
                    borderRadius: '4px',
                },
            }}
        />
    );
};

const EditableNumberCell = ({ id, field, value, label, saveEditedField, darkMode }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'stage':
                return {
                    color: darkMode ? '#ab47bc' : '#6a1b9a',
                };
            case 'fid':
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
            default:
                return {
                    color: darkMode ? '#9c27b0' : '#9c27b0',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value || '');

    const handleNumberChange = (e) => {
        const inputValue = e.target.value;
        // Разрешаем только цифры и пустую строку
        if (inputValue === '' || /^\d+$/.test(inputValue)) {
            setLocalValue(inputValue);
        }
    };

    const handleBlur = () => {
        const numericValue = localValue ? parseInt(localValue, 10) : value;
        saveEditedField({
            rowId: id,
            field: field,
            value: numericValue,
            isValueChanged: value !== numericValue
        });
    };

    const handleClear = () => {
        setLocalValue('');
    };

    const handleSave = () => {
        handleBlur();
    };

    return (
        <TextField
            fullWidth
            size="small"
            value={localValue}
            onChange={handleNumberChange}
            onBlur={handleBlur}
            autoFocus

            sx={{
                '& .MuiOutlinedInput-input': {
                    p: '5px',
                    cursor: 'text',
                    color: darkMode ? '#ffffff' : '#333',
                    fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldColors.color,
                    borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                    fontSize: '0.875rem',
                    '&.Mui-focused': {
                        color: fieldColors.color,
                    }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                },
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                '& .MuiInputBase-root': {
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    minWidth: '60px',
                    // width: '100%'
                },
                '& .MuiInputBase-input': {
                    borderRadius: '4px',
                },
            }}
        />
    );
};

const EditableDateCell = ({ id, field, value, label, saveEditedField, darkMode }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'plan_start':
            case 'fact_start':
            case 'plan_end':
            case 'fact_end':
                return {
                    color: darkMode ? '#64b5f6' : '#1976d2',
                };
            default:
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value)

    // Преобразуем строку даты в объект Date для DatePicker
    const getDateFromString = (dateString) => {
        if (!dateString) return null;
        try {
            // Если дата в формате "YYYY-MM-DD" (из базы данных)
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return new Date(dateString);
            }

            // Если дата в формате "HH:MM DD-MM-YYYY"
            const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
            if (dateTimeMatch) {
                const [, hours, minutes, day, month, year] = dateTimeMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Если дата в формате "DD-MM-YYYY"  
            const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (dateMatch) {
                const [, day, month, year] = dateMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Попытка парсинга как обычной даты
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch {
            return null;
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            setLocalValue(formattedDate);
        } else {
            setLocalValue('');
        }
    };

    const handleBlur = () => {
        saveEditedField({ rowId: id, field: field, value: localValue, isValueChanged: value !== localValue });

    };

    return (
        <Box sx={{ position: 'relative' }}>
            {(() => {
                try {
                    return (
                        <DatePicker
                            selected={getDateFromString(localValue)}
                            // onCalendarClose={handleDateChange}
                            onChange={handleDateChange}
                            onBlur={handleBlur}
                            onCalendarClose={handleBlur}
                            // onClickOutside={handleBlur}

                            locale="ru"
                            dateFormat="dd.MM.yyyy"
                            // placeholderText={label}
                            autoFocus
                            // shouldCloseOnSelect={false}

                            // isClearable
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={10}
                            scrollableYearDropdown
                            autoComplete="off"
                            customInput={
                                <TextField
                                    // fullWidth
                                    // label={label}
                                    size="small"
                                    // variant="standard"
                                    // InputProps={{
                                    //     endAdornment: (
                                    //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    //             {localValue && (
                                    //                 <IconButton
                                    //                     size="small"
                                    //                     onClick={e => {
                                    //                         e.stopPropagation();
                                    //                         setLocalValue('');
                                    //                     }}
                                    //                     edge="end"
                                    //                     tabIndex={-1}
                                    //                     sx={{
                                    //                         color: darkMode ? '#f44336' : '#d32f2f',
                                    //                         p: 0.1,
                                    //                         '&:hover': {
                                    //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                    //                         },
                                    //                     }}
                                    //                 >
                                    //                     <ClearIcon fontSize="small" />
                                    //                 </IconButton>
                                    //             )}
                                    //             <IconButton
                                    //                 size="small"
                                    //                 onClick={e => {
                                    //                     e.stopPropagation();
                                    //                     handleBlur();
                                    //                 }}
                                    //                 edge="end"
                                    //                 tabIndex={-1}
                                    //                 sx={{
                                    //                     color: darkMode ? '#4caf50' : '#2e7d32',
                                    //                     p: 0.1,
                                    //                     '&:hover': {
                                    //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                    //                     }
                                    //                 }}
                                    //             >
                                    //                 <SaveIcon fontSize="small" />
                                    //             </IconButton>
                                    //         </Box>
                                    //     ),
                                    // }}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            p: '5px',
                                            cursor: 'pointer',
                                            color: darkMode ? '#ffffff' : '#333',
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: fieldColors.color,
                                            borderWidth: '2px',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                            fontSize: '0.875rem',
                                            '&.Mui-focused': {
                                                color: fieldColors.color,
                                            }
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                            borderRadius: '4px',
                                        },
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        '& .MuiInputBase-root': {
                                            // pr: 0,
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                            borderRadius: '4px',
                                            // minWidth: '140px',
                                            width: '90px'
                                        },
                                        '& .MuiInputBase-input': {
                                            borderRadius: '4px',
                                        },
                                    }}
                                />
                            }
                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                        />
                    );
                } catch (error) {
                    console.error('DatePicker error:', error);
                    return null;
                }
            })()}
        </Box>
    );
}



// Функция для получения иконки статуса
const getStatusIcon = (iconName) => {
    switch (iconName) {
        case 'notifications':
            return <NotificationsIcon sx={{ fontSize: '1rem' }} />;
        case 'check':
            return <CheckIcon sx={{ fontSize: '1rem' }} />;
        case 'settings':
            return <SettingsIcon sx={{ fontSize: '1rem' }} />;
        case 'send':
            return <SendIcon sx={{ fontSize: '1rem' }} />;
        case 'close':
            return <CloseIcon sx={{ fontSize: '1rem' }} />;
        default:
            return <NotificationsIcon sx={{ fontSize: '1rem' }} />;
    }
};


const statuses = {
    1: { stage: 'Не начато', stage_icon: 'notifications', stage_color: '#FF6500', argb: 'FFFF6500' },
    2: { stage: 'Устранено', stage_icon: 'check', stage_color: '#017421', argb: 'FF017421' },
    3: { stage: 'В работе', stage_icon: 'settings', stage_color: '#1E90FF', status: 'Замечение принято', argb: 'FF1E90FF' },
    4: { stage: 'В ожидании', stage_icon: 'settings', stage_color: '#FFA500', status: 'Замечение отклонено', argb: 'FFFFA500' },
    5: { stage: 'Отправлено', stage_icon: 'send', stage_color: '#00ff00', argb: 'FF00ff00' },
    6: { status: 'Документация запрошена', argb: 'FF000000' },
    7: { status: 'Замечание принято частично', argb: 'FF000000' },
    8: { stage: 'Снято', stage_icon: 'close', argb: 'FF000000' },
    9: { status: 'Обсудить с экспертом', argb: 'FF000000' }
};

// Complexity options
const complexityOptions = [
    { id: 'I', value: 'I', label: 'Низкая' },
    { id: 'II', value: 'II', label: 'Средняя' },
    { id: 'III', value: 'III', label: 'Высокая' }
];



const getFileIcon = (ext) => {
    const iconProps = { sx: { width: 20, height: 20 } };
    switch (ext) {
        case 'docx':
            return <DescriptionIcon {...iconProps} sx={{ ...iconProps.sx, color: '#3972C9' }} />;
        case 'xlsx':
            return <TableChartIcon {...iconProps} sx={{ ...iconProps.sx, color: '#217346' }} />;
        case 'pdf':
            return <PictureAsPdfIcon {...iconProps} sx={{ ...iconProps.sx, color: '#BF3D2B' }} />;
        case 'jpg':
        case 'png':
            return <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: '#C98D00' }} />;
        default:
            return <DescriptionIcon {...iconProps} sx={{ ...iconProps.sx, color: 'white' }} />;
    }
};

// Функция-обертка для фильтрации видимых ячеек
function filterVisibleCells(cells, visibleColumns) {
    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    // Если все столбцы видимы, возвращаем все ячейки
    if (visibleColumnsArray.length === columns.length) {
        return cells;
    }

    // Фильтруем ячейки по индексу видимых столбцов
    return cells.filter((cell, index) => {
        // Индекс соответствует порядку столбцов в массиве columns
        return index < columns.length && visibleColumns[columns[index].id];
    });
}

function rowContent(
    _index,
    row,
    {
        editMode,
        editing,
        setEditingField,
        openHistoryDialog,
        saveEditedField,
        darkMode,
        editedFields,
        selectedRows,
        handleRowSelection,
        handleOpenEditDialog,
        statusOptions,
        canEditReady,
        canEditSuccess,
        visibleColumns,
        users
    }
) {
    // Если это padding элемент, возвращаем пустую строку
    if (row.isPadding) {
        // Получаем только видимые столбцы
        const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);
        return (
            <React.Fragment>
                {visibleColumnsArray.map((column) => (
                    <TableCell key={column.id} sx={{
                        height: '20px',
                        minHeight: '20px',
                        padding: '4px 8px',
                        border: 'none',
                        lineHeight: '20px'
                    }} />
                ))}
            </React.Fragment>
        );
    }




    // Определяем цвет фона для чередования строк
    const isRowEven = _index % 2 !== 0;
    // const rowBackgroundColor = isRowEven ? { backgroundColor: darkMode ? '#151a01' : '#fff7e1' } : {};

    // Проверяем, что у нас есть данные строки
    if (!row) return null;

    // Проверяем, была ли строка изменена
    const isRowEdited = Object.keys(editedFields).length > 0;

    // Общий стиль для всех TableCell
    const cellStyle = {
        minHeight: '40px',
        height: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
        color: darkMode ? '#f5f7fa' : '#333',
        borderBottom: darkMode
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.08)',
        borderRight: darkMode
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(0, 0, 0, 0.05)',
        fontSize: { xs: '0.7rem', md: '0.8rem' },
        // ...rowBackgroundColor,
        ...(isRowEdited && {
            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
            // borderLeft: '4px solid #4caf50'
        })
    };




    return (
        <React.Fragment>
            {/* № замечания */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.num_remarks}
                </Typography>
            </TableCell>

            {/* Стадия */}
            <TableCell align="center" sx={{
                ...cellStyle,
                fontWeight: 'bold',
                color: statuses[row.stage]?.stage_color || (darkMode ? 'white' : 'black'),
                whiteSpace: 'nowrap'
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    {getStatusIcon(statuses[row.stage]?.stage_icon)}
                    {statuses[row.stage]?.stage}
                </Box>
            </TableCell>

            {/* Сложность */}
            <TableCell sx={{
                ...cellStyle,
                textAlign: 'center',
                minWidth: '120px',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(233, 152, 30, 0.15)' : 'rgba(194, 140, 24, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'complexity' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'complexity' ?
                    <EditableSelectCell
                        groupId={row.conclusionId}
                        id={row.id}
                        field={editing.field}
                        label={'Выберите сложность'}
                        value={complexityOptions.find(co => co.value === (editedFields.complexity || row.complexity || 'II'))}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={complexityOptions}
                        saveValue={(value) => value.value}
                        multiple={false}
                    />
                    :
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                        <Chip
                            label={complexityOptions.find(co => co.value === (editedFields.complexity || row.complexity || 'II'))?.label}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                                color: darkMode ? '#D85AED' : '#7E20B8',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 'auto',
                                minHeight: '24px',
                                maxWidth: '100%',
                                cursor: editMode ? 'pointer' : 'auto',
                                '&:hover': editMode ? {
                                    bgcolor: darkMode ? 'rgba(171, 71, 188, 0.3)' : 'rgba(106, 27, 154, 0.2)',
                                    transform: 'scale(1.02)',
                                } : {},
                                transition: 'all 0.2s ease',
                                '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textAlign: 'center',
                                    lineHeight: '1.2',
                                    padding: '4px 8px',
                                },
                            }}
                        />
                    </Typography>
                }
            </TableCell>

            {/* Замечания */}
            <TableCell align="center" sx={{
                ...cellStyle,
                textAlign: 'center'
            }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, lineHeight: 1.1 }}>
                    {row.task}
                </Typography>
            </TableCell>

            {/* Статус замечания */}
            <TableCell sx={{
                ...cellStyle,
                textAlign: 'center',
                minWidth: '180px',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(233, 152, 30, 0.15)' : 'rgba(194, 140, 24, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'status_remark' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'status_remark' ?
                    <EditableSelectCell
                        groupId={row.conclusionId}
                        id={row.id}
                        field={editing.field}
                        label={'Выберите статус замечания'}
                        value={statusOptions.find(so => so.id === String(editedFields.status_remark || row.status_remark))}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={statusOptions}
                        saveValue={(value) => value.id}
                        multiple={false}
                    />
                    :
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, lineHeight: 1.1 }}>
                        <Chip
                            label={statusOptions.find(so => so.id === String(editedFields.status_remark || row.status_remark))?.name || ''}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                                color: darkMode ? '#7486F0' : '#4154C4',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 'auto',
                                minHeight: '24px',
                                maxWidth: '100%',
                                cursor: editMode ? 'pointer' : 'auto',
                                '&:hover': editMode ? {
                                    bgcolor: darkMode ? 'rgba(92, 107, 192, 0.3)' : 'rgba(57, 73, 171, 0.2)',
                                    transform: 'scale(1.02)',
                                } : {},
                                transition: 'all 0.2s ease',
                                '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textAlign: 'center',
                                    lineHeight: '1.2',
                                    padding: '4px 8px',
                                },
                            }}
                        />
                    </Typography>
                }
            </TableCell>

            {/* Ответ на замечание */}
            <TableCell sx={{
                ...cellStyle,
                textAlign: 'center',
                minWidth: '250px',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(233, 152, 30, 0.15)' : 'rgba(194, 140, 24, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'response_remark' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'response_remark' ?
                    <EditableTextAreaCell
                        groupId={row.conclusionId}
                        id={row.id}
                        field={editing.field}
                        label={'Ответ на замечание'}
                        value={row.response_remark || ''}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, lineHeight: 1.1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {editedFields.response_remark || row.response_remark || ''}
                    </Typography>
                }
            </TableCell>

            {/* Результат */}
            <TableCell sx={{
                ...cellStyle,
                textAlign: 'center',
                minWidth: '250px',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(233, 152, 30, 0.15)' : 'rgba(194, 140, 24, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'result' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'result' ?
                    <EditableTextAreaCell
                        groupId={row.conclusionId}
                        id={row.id}
                        field={editing.field}
                        label={'Результат'}
                        value={row.result || ''}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, lineHeight: 1.1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {editedFields.result || row.result || ''}
                    </Typography>
                }
            </TableCell>

            {/* Исполнитель */}
            <TableCell align="center" sx={{
                ...cellStyle,
                textAlign: 'center',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(233, 152, 30, 0.15)' : 'rgba(194, 140, 24, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'responsible' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'responsible' ?
                    <EditableSelectCell
                        groupId={row.conclusionId}
                        id={row.id}
                        field={editing.field}
                        label={'Выберите исполнителей'}
                        value={users.filter(u => editedFields.responsible ? editedFields.responsible.includes(String(u.id)) : row.responsible.includes(String(u.id)))}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={users}
                        multiple={true}
                        saveValue={(value) => value.map(v => String(v.id))}
                    />
                    :
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {(users.filter(u => editedFields.responsible ? editedFields.responsible.includes(String(u.id)) : row.responsible.includes(String(u.id))) || []).map((person, index) => (
                            <Chip
                                key={index}
                                label={String(person?.name || '')}
                                size="small"
                                onClick={() => { }}
                                sx={{
                                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                    color: darkMode ? '#ffb74d' : '#CF6900',
                                    fontSize: '0.7rem',
                                    height: 'auto',
                                    minHeight: '24px',
                                    maxWidth: '100%',
                                    cursor: editMode ? 'pointer' : 'auto',
                                    '&:hover': editMode ? {
                                        bgcolor: darkMode ? 'rgba(245, 124, 0, 0.3)' : 'rgba(245, 124, 0, 0.2)',
                                        transform: 'scale(1.02)',
                                    } : {},
                                    transition: 'all 0.2s ease',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        textAlign: 'center',
                                        lineHeight: '1.2',
                                        padding: '4px 8px',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                }
            </TableCell>

            {/* Редактор */}
            <TableCell align="center" sx={{
                ...cellStyle,
                textAlign: 'center'
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#f5f7fa' : '#333',
                        fontWeight: 500,
                        fontSize: '0.75rem'
                    }}>
                        {/* {String(row.creator?.name || '')} */}
                        {row.editorVio?.short_fio} ({row.editorVio?.post_for_select})
                    </Typography>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontSize: '0.75rem',
                    }}>
                        {/* {formatDate(row.created_at)} */}
                        {row.edited}
                    </Typography>
                </Box>

            </TableCell>

            {/* Создатель */}
            <TableCell align="center" sx={{
                ...cellStyle,
                textAlign: 'center'
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#f5f7fa' : '#333',
                        fontWeight: 500,
                        fontSize: '0.75rem'
                    }}>
                        {/* {String(row.creator?.name || '')} */}
                        {row.creatorVio?.short_fio} ({row.creatorVio?.post_for_select})
                    </Typography>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontSize: '0.75rem',
                    }}>
                        {/* {formatDate(row.created_at)} */}
                        {row.created}
                    </Typography>
                </Box>


            </TableCell>
        </React.Fragment>
    );
}














// Утилиты для работы с localStorage
const STORAGE_KEYS = {
    CONCLUSIONS_SETTINGS: 'conclusions_settings'
};

const getStoredSettings = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.CONCLUSIONS_SETTINGS);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const storeSettings = (settings) => {
    try {
        const current = getStoredSettings();
        const updated = { ...current, ...settings };
        localStorage.setItem(STORAGE_KEYS.CONCLUSIONS_SETTINGS, JSON.stringify(updated));
    } catch (error) {
        console.warn('Failed to save conclusions settings:', error);
    }
};

const parseDate = (str) => {
    if (!str) return null
    const [timePart, datePart] = str.split(' ');
    const [day, month, year] = datePart.split('.').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    // Создаем объект Date (месяцы в JS начинаются с 0)
    return new Date(year, month - 1, day, hours, minutes);
};

const ConclusionsView = ({
    isModal = false,
    onClose = null,
    map = null,
    fromStartPage = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector(state => state.state.user);
    const darkMode = useSelector(state => state.state.darkMode);
    const dispatch = useDispatch()
    

    // Получаем сохраненные настройки
    const storedSettings = getStoredSettings();

    const data = useSelector(selectConclusionsForUser);
    const rulesMap = useSelector(selectRulesMap);
    const checklistFilter = useSelector(selectChecklistFilter);
    const [filteredData, setFilteredData] = useState([]);
    const [groupedData, setGroupedData] = useState({ groupCounts: [], groupedData: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([])
    const [conclusionsProjects, setConclusionsProjects] = useState(null);

    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [imageViewerActiveIndex, setImageViewerActiveIndex] = useState(0);
    const [images, setImages] = useState([]);
    const [editor, setEditor] = useState(null);

    const objects = useMemo(() => {
        if (!data) return []
        return data.map(d => ({
            ...d,
            name: d.description,
            tbl: null
        }))
    }, [data])

    const [canEditReady, canEditSuccess] = useMemo(() => {
        return [
            user.roles.some(v => ['project_manager', 'smr_foreman'].includes(v)),
            user.roles.includes('authors_control')
        ];
    }, [user]);

    // Состояния для поиска и фильтрации
    const [searchQuery, setSearchQuery] = useState('');
    // 'all', 'my', 'stage'
    const [selectedObjectId, setSelectedObjectId] = useState(null);
    const [selectedObjects, setSelectedObjects] = useState(storedSettings.selectedObjects ?? []);

    // Состояние для фильтрации по дереву проектов
    const [selectedFilters, setSelectedFilters] = useState(() => {
        const stored = storedSettings.selectedFilters;
        return stored ? new Set(stored) : new Set();
    });
    const [treeSearchQuery, setTreeSearchQuery] = useState('');

    // Состояния для сортировки
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'


    const [selectedObject, setSelectedObject] = useState(null);
    const [editMode, setEditMode] = useState(false)
    const [editing, setEditing] = useState({ id: null, field: null })
    const [editedRows, setEditedRows] = useState([])
    const [hasRejectedRows, setHasRejectedRows] = useState([])
    const [rejectionReason, setRejectionReason] = useState('');

    // Состояние для управления видимостью столбцов
    const [visibleColumns, setVisibleColumns] = useState(
        storedSettings.visibleColumns ?? columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
    );
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [columnWidths, setColumnWidths] = useState({});

    // Временные данные для фильтров (можно заменить на реальные данные из Redux)
    const [responsible, setResponsible] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);


    // Состояния для продвинутой фильтрации
    const [advancedFilters, setAdvancedFilters] = useState(storedSettings.advancedFilters ?? []);
    const [filterMode, setFilterMode] = useState(storedSettings.filterMode ?? 'objects'); // 'objects' | 'codes'
    const availableFilters = useMemo(() => ([
        { id: 'object_name', label: 'Наименование объекта', type: 'text' },
        { id: 'object_conclusion', label: 'Объект - Заключение', type: 'text' },
        { id: 'object_attach_length', label: 'Объект - Вложения (кол-во)', type: 'number' },
        { id: 'object_attach_type', label: 'Объект - Вложения (тип)', type: 'multi-select', options: [{ id: 'docx', name: 'docx', label: 'docx' }, { id: 'xlsx', name: 'xlsx', label: 'xlsx' }, { id: 'pdf', name: 'pdf', label: 'pdf' }, { id: 'jpg', name: 'jpg', label: 'jpg' }, { id: 'png', name: 'png', label: 'png' }] },
        { id: 'object_proc', label: 'Объект - Готовность', type: 'number' },
        { id: 'object_sender', label: 'Объект - Отправитель', type: 'text' },
        { id: 'object_remarks_count', label: 'Объект - Количество замечаний', type: 'number' },
        { id: 'object_repeated_remarks_count', label: 'Объект - Количество повторных замечаний', type: 'number' },
        { id: 'object_ext_date', label: 'Объект - От даты', type: 'date' },
        { id: 'object_deadline', label: 'Объект - Срок', type: 'date' },
        { id: 'object_status', label: 'Объект - Статус', type: 'multi-select', operator: 'equals', options: Object.entries(statuses).map(([key, value]) => ({ id: key, name: value.stage, label: value.stage })).filter(f => f.name) },
        { id: 'num_remarks', label: 'Замечание - №', type: 'number' },
        { id: 'stage', label: 'Замечание - Стадия', type: 'number', options: Object.entries(statuses).map(([key, value]) => ({ id: key, name: value.stage, label: value.stage })).filter(f => f.name) },
        { id: 'complexity', label: 'Замечание - Сложность', type: 'multi-select', operator: 'equals', options: complexityOptions.map(c => ({ id: c.value, name: c.label, label: c.label })) },
        { id: 'status', label: 'Замечание - Статус замечания', type: 'multi-select', operator: 'equals', options: statusOptions.map(s => ({ id: s.id, name: s.name, label: s.name })) },
        { id: 'task', label: 'Замечание - Замечания', type: 'text' },
        { id: 'response_remark', label: 'Замечание - Ответ на замечание', type: 'text' },
        { id: 'result', label: 'Замечание - Результат', type: 'text' },
        { id: 'responsible', label: 'Замечание - Исполнитель', type: 'multi-select', options: users.map(u => ({ id: u.id, name: u.name, label: u.name })) },
        { id: 'editor', label: 'Замечание - Редактор', type: 'multi-select', options: users.map(u => ({ id: u.id, name: u.name, label: u.name })) },
        { id: 'edited', label: 'Замечание - Дата редактирования', type: 'date' },
        { id: 'creator', label: 'Замечание - Создатель', type: 'multi-select', options: users.map(u => ({ id: u.id, name: u.name, label: u.name })) },
        { id: 'created', label: 'Замечание - Дата создания', type: 'date' }
    ]), [statusOptions, users, complexityOptions]);


    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Состояние для меню экспорта
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Состояние для меню фильтрации проектов
    const [projectsFilterAnchor, setProjectsFilterAnchor] = useState(null);
    const projectsFilterPopperRef = useRef(null);

    // Состояние для выбранных строк
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Функции для работы с чекбоксами дерева проектов
    const handleProjectToggle = (projectId) => {
        const newSelectedFilters = new Set(selectedFilters);
        if (newSelectedFilters.has(projectId)) {
            newSelectedFilters.delete(projectId);
            newSelectedFilters.delete(`${projectId}-0`);
        } else {
            newSelectedFilters.add(projectId);
            newSelectedFilters.add(`${projectId}-0`);
        }
        setSelectedFilters(newSelectedFilters);
    };

    const handleStageToggle = (projectId, stageId) => {
        const newSelectedFilters = new Set(selectedFilters);
        const stageKey = `${projectId}-${stageId}`;
        if (newSelectedFilters.has(stageKey)) {
            newSelectedFilters.delete(stageKey);
        } else {
            newSelectedFilters.add(stageKey);
        }
        setSelectedFilters(newSelectedFilters);
    };

    const handleObjectToggle = (projectId, stageId, objectId) => {
        const newSelectedFilters = new Set(selectedFilters);
        const objectKey = `${projectId}-${stageId}-${objectId}`;
        if (newSelectedFilters.has(objectKey)) {
            newSelectedFilters.delete(objectKey);
        } else {
            newSelectedFilters.add(objectKey);
        }
        setSelectedFilters(newSelectedFilters);
    };

    const clearAllFilters = () => {
        setSelectedFilters(new Set());
    };

    const selectAllFilters = () => {
        if (!conclusionsProjects) return;
        const allKeys = new Set();

        Object.entries(conclusionsProjects).forEach(([projectId, project]) => {
            allKeys.add(projectId);
            Object.entries(project.stages).forEach(([stageId, stage]) => {
                allKeys.add(`${projectId}-${stageId}`);
                Object.keys(stage.objects).forEach(objectId => {
                    allKeys.add(`${projectId}-${stageId}-${objectId}`);
                });
            });
        });

        setSelectedFilters(allKeys);
    };

    const getDisplayText = () => {
        if (selectedFilters.size === 0) {
            return "Выбор проектов";
        }
        return `Выбрано: ${selectedFilters.size}`;
    };

    // Преобразуем данные conclusions_projects в формат для SimpleTreeView
    const treeData = useMemo(() => {
        if (!conclusionsProjects) return [];
        console.log(conclusionsProjects)

        const filterTreeData = (data, searchQuery) => {
            if (!searchQuery) return data;

            return data.filter(item => {
                const labelMatches = item.label.toLowerCase().includes(searchQuery.toLowerCase());
                const childrenFiltered = item.children ? filterTreeData(item.children, searchQuery) : [];

                if (labelMatches || childrenFiltered.length > 0) {
                    return {
                        ...item,
                        children: childrenFiltered
                    };
                }
                return false;
            }).map(item => ({
                ...item,
                children: item.children ? filterTreeData(item.children, searchQuery) : undefined
            }));
        };

        const allData = Object.entries(conclusionsProjects).map(([projectId, project]) => ({
            id: projectId,
            level: 'projects',
            label: project.label,
            children: [...Object.entries(project.stages).map(([stageId, stage]) => {
                if (stageId != 0) {
                    return {
                        id: `${projectId}-${stageId}`,
                        label: stage.label,
                        level: 'stages',
                        children: Object.entries(stage.objects).map(([objectId, objectName]) => ({
                            id: `${projectId}-${stageId}-${objectId}`,
                            label: objectName,
                            level: 'objects',
                        }))
                    }
                }
                return null
            }).filter(f => f), ...(Object.entries(project.stages[0]?.objects) || []).map(([objectId, objectName]) => ({
                id: `${projectId}-0-${objectId}`,
                label: objectName,
                level: 'objects'
            }))]
        }));

        return filterTreeData(allData, treeSearchQuery);
    }, [conclusionsProjects, treeSearchQuery]);

    // Рекурсивная функция для добавления/удаления всех дочерних элементов
    const toggleChildrenRecursively = (item, selectedFiltersSet, isAdding) => {
        if (item.children && item.children.length > 0) {
            item.children.forEach(child => {
                if (isAdding) {
                    selectedFiltersSet.add(child.id);
                } else {
                    selectedFiltersSet.delete(child.id);
                }
                // Рекурсивно обрабатываем дочерние элементы
                toggleChildrenRecursively(child, selectedFiltersSet, isAdding);
            });
        }
    };

    // Функция для рендеринга дерева с чекбоксами
    const renderTreeItem = (item) => {
        const isSelected = selectedFilters.has(item.id);
        const count = [...selectedFilters].filter(f => f.startsWith(`${item.id}-`))?.length
        const isIndeterminate = item.level !== 'stages' ? false : (count > 0 && count < item.children.length)

        return (
            <TreeItem
                key={item.id}
                itemId={item.id}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 0 }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            indeterminate={isIndeterminate}
                            onChange={(e) => {
                                e.stopPropagation();
                                const newSelectedFilters = new Set(selectedFilters);
                                if (isSelected) {
                                    newSelectedFilters.delete(item.id);
                                    // Рекурсивно удаляем все дочерние элементы
                                    toggleChildrenRecursively(item, newSelectedFilters, false);
                                } else {
                                    newSelectedFilters.add(item.id);
                                    // Рекурсивно добавляем все дочерние элементы
                                    toggleChildrenRecursively(item, newSelectedFilters, true);
                                }
                                setSelectedFilters(newSelectedFilters);
                            }}
                            sx={{
                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2'
                                },
                                padding: '4px',
                                mr: 0.5
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: item.children ? 600 : 400,
                                fontSize: item.children ? '0.875rem' : '0.8rem',
                                color: darkMode ? '#f5f7fa' : '#333',
                                lineHeight: 1.2
                            }}
                        >
                            {item.level === 'stages' ? `${item.label} (${item.children?.length || 0})` : item.label}
                        </Typography>
                    </Box>
                }
            >
                {item.children?.map(child => renderTreeItem(child))}
            </TreeItem>
        );
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await $authHost.get('/apig/users');
                const { success, data } = response.data;

                setUsers(success ? data.map(d => ({
                    id: d.vio_id || 0,
                    name: d.display_name
                })).filter(f => f.id) : []);
            } catch (e) {
                setUsers([])
                console.log(e)
                setError(e.message);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchConclusionsProjects = async () => {
            try {
                const response = await $authHost.get('/apig/state/conclusions_projects');
                let { data } = response
                if (user.roles?.includes('smr_foreman')) {
                    const filteredData = {}
                    Object.entries(data).forEach(([key, value]) => {
                        if (value.is_smr) {
                            filteredData[key] = value
                        }
                    })
                    console.log(filteredData)
                    data = filteredData
                }
                setConclusionsProjects(data);
            } catch (e) {
                setConclusionsProjects(null);
                console.log('Error fetching conclusions_projects:', e);
            }
        };
        fetchConclusionsProjects();
    }, [user]);

    // Закрываем меню при клике вне его
    useEffect(() => {
        if (!projectsFilterAnchor) return;

        const handleClickOutside = (event) => {
            // Проверяем, был ли клик внутри TextField (anchor) или внутри Popper (Paper)
            const clickedInsideAnchor = projectsFilterAnchor && projectsFilterAnchor.contains(event.target);
            const clickedInsidePopper = projectsFilterPopperRef.current && projectsFilterPopperRef.current.contains(event.target);
            
            // Если клик был вне обоих элементов, закрываем меню
            if (!clickedInsideAnchor && !clickedInsidePopper) {
                setProjectsFilterAnchor(null);
            }
        };

        // Используем небольшую задержку, чтобы клик на TextField не закрывал меню сразу
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [projectsFilterAnchor]);

    // Fetch status options from backend
    useEffect(() => {
        const fetchStatuses = async () => {
            try {


                const response = await $authHost.get('/apig/state/conclusions/statuses');
                const data = response.data;
                // Transform the data to match expected format
                const statausData = data.map(item => ({
                    id: String(item.id),
                    name: item.status
                }));
                setStatusOptions(statausData);

            } catch (error) {
                console.error('Error fetching statuses:', error);
            }
        };


        fetchStatuses();
    }, []);

    // Функция для фильтрации и сортировки данных
    const filterAndSortData = useCallback((rawData, search, selectedFilters, advancedFilters, sortBy, sortDirection, filterMode) => {
        let filtered = [...rawData];
        // Поиск
        if (search.trim()) {
            // Разбиваем поисковый запрос на отдельные слова и фильтруем пустые
            const searchTerms = search.toLowerCase().split(/\s+/).filter(term => term.length > 0);

            filtered = filtered.map(item => {
                // Для conclusions ищем в tbl (замечаниях)
                const filteredTbl = item.tbl ? item.tbl.filter(remark =>
                    searchTerms.every(word =>
                        `${remark.num_remarks} ${remark.task || ''} ${remark.response_remark || ''} ${remark.result || ''} ${remark.status_remark || ''} ${remark.complexity || ''}`.toLowerCase().includes(word))
                ) : [];

                return {
                    ...item,
                    tbl: filteredTbl
                }
            }).filter(item => item.tbl && item.tbl.length > 0);
        }

        // Фильтрация по проектам (для conclusions показываем все)
        if (selectedFilters) {
            filtered = filtered.map(item => {

                // Проверяем, есть ли у заключения выбранные проекты
                // console.log(item.project_id)
                // if (![...selectedFilters].find(sf => sf.startsWith(item.project_id))) {

                //     return null;
                // }

                let tbl = item.tbl

                if (tbl) {
                    tbl = tbl.filter(t => {
                        return t.stages?.some(({ obj, stage }) => {
                            const stageKey = `${item.project_id}-${stage}`;
                            const objectKey = `${item.project_id}-${stage}-${obj}`;
                            console.log(stageKey, objectKey)
                            return selectedFilters.has(objectKey) || selectedFilters.has(stageKey)
                        })
                    })
                }

                if (tbl.length === 0 && !selectedFilters.has(String(item.project_id))) {
                    return null

                }

                return { ...item, tbl }
            }).filter(f => f);
        }
        console.log(selectedFilters)
        console.log(filtered)

        // Продвинутая фильтрация (упрощенная для conclusions)
        if (advancedFilters && advancedFilters.length > 0) {
            advancedFilters.forEach(filter => {
                console.log(filter)
                switch (filter.id) {
                    case 'num_remarks':
                        if (filter.value && filter.value !== '') {
                            const filterId = Number(filter.value);
                            if (!isNaN(filterId)) {
                                filtered = filtered.map(item => {
                                    return {
                                        ...item,
                                        tbl: item.tbl ? item.tbl.filter(remark => {
                                            const itemId = Number(remark.num_remarks);
                                            if (isNaN(itemId)) return false;

                                            switch (filter.operator) {
                                                case 'equals':
                                                    return itemId === filterId;
                                                case 'greater':
                                                    return itemId > filterId;
                                                case 'less':
                                                    return itemId < filterId;
                                                default:
                                                    return true;
                                            }
                                        }) : []
                                    }
                                }).filter(item => item.tbl && item.tbl.length > 0);
                            }
                        }
                        break;

                    case 'stage':
                        if (filter.value && filter.value !== '') {
                            const stageValue = Number(filter.value);
                            if (!isNaN(stageValue)) {
                                filtered = filtered.map(item => {
                                    return {
                                        ...item,
                                        tbl: item.tbl ? item.tbl.filter(remark => remark.stage === stageValue) : []
                                    }
                                }).filter(item => item.tbl && item.tbl.length > 0);
                            }
                        }
                        break;
                    case 'complexity':
                        if (filter.value && filter.value.length > 0) {
                            const complexityValues = filter.value.map(c => c.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        if (filter.logic === 'and') {
                                            return complexityValues.every(complexity => remark.complexity === complexity);
                                        } else {
                                            return complexityValues.some(complexity => remark.complexity === complexity);
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'status':
                        if (filter.value && filter.value.length > 0) {
                            const statusValues = filter.value.map(s => s.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        if (filter.logic === 'and') {
                                            return statusValues.every(status => remark.status_remark === status);
                                        } else {
                                            return statusValues.some(status => remark.status_remark === status);
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'task':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        const taskValue = (remark.task || '').toLowerCase();
                                        switch (filter.operator) {
                                            case 'contains':
                                                return taskValue.includes(searchValue);
                                            case 'not_contains':
                                                return !taskValue.includes(searchValue);
                                            case 'equals':
                                                return taskValue === searchValue;
                                            case 'not_equals':
                                                return taskValue !== searchValue;
                                            default:
                                                return true;
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'response_remark':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        const responseValue = (remark.response_remark || '').toLowerCase();
                                        switch (filter.operator) {
                                            case 'contains':
                                                return responseValue.includes(searchValue);
                                            case 'not_contains':
                                                return !responseValue.includes(searchValue);
                                            case 'equals':
                                                return responseValue === searchValue;
                                            case 'not_equals':
                                                return responseValue !== searchValue;
                                            default:
                                                return true;
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'result':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        const resultValue = (remark.result || '').toLowerCase();
                                        switch (filter.operator) {
                                            case 'contains':
                                                return resultValue.includes(searchValue);
                                            case 'not_contains':
                                                return !resultValue.includes(searchValue);
                                            case 'equals':
                                                return resultValue === searchValue;
                                            case 'not_equals':
                                                return resultValue !== searchValue;
                                            default:
                                                return true;
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    // Фильтры для объектов
                    case 'object_name':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.filter(item => {
                                const objectName = (item.description || '').toLowerCase();
                                switch (filter.operator) {
                                    case 'contains':
                                        return objectName.includes(searchValue);
                                    case 'not_contains':
                                        return !objectName.includes(searchValue);
                                    case 'equals':
                                        return objectName === searchValue;
                                    case 'not_equals':
                                        return objectName !== searchValue;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'object_conclusion':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.filter(item => {
                                const conclusion = (item.name_conclusion || '').toLowerCase();
                                switch (filter.operator) {
                                    case 'contains':
                                        return conclusion.includes(searchValue);
                                    case 'not_contains':
                                        return !conclusion.includes(searchValue);
                                    case 'equals':
                                        return conclusion === searchValue;
                                    case 'not_equals':
                                        return conclusion !== searchValue;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'object_attach_length':
                        if (filter.value && filter.value !== '') {
                            const filterValue = Number(filter.value);
                            if (!isNaN(filterValue)) {
                                filtered = filtered.filter(item => {
                                    const attachLength = item.attach ? item.attach.length : 0;
                                    switch (filter.operator) {
                                        case 'equals':
                                            return attachLength === filterValue;
                                        case 'greater':
                                            return attachLength > filterValue;
                                        case 'less':
                                            return attachLength < filterValue;
                                        default:
                                            return true;
                                    }
                                });
                            }
                        }
                        break;

                    case 'object_attach_type':
                        if (filter.value && filter.value.length > 0) {
                            const attachTypes = filter.value.map(t => t.id);
                            filtered = filtered.filter(item => {
                                if (!item.attach || item.attach.length === 0) return false;
                                const itemTypes = item.attach.map(att => att.ext).filter(Boolean);

                                if (filter.logic === 'and') {
                                    return attachTypes.every(type => itemTypes.includes(type));
                                } else {
                                    return attachTypes.some(type => itemTypes.includes(type));
                                }
                            });
                        }
                        break;

                    case 'object_proc':
                        if (filter.value && filter.value !== '') {
                            const filterValue = Number(filter.value);
                            if (!isNaN(filterValue)) {
                                filtered = filtered.filter(item => {
                                    const proc = Number(item.proc) || 0;
                                    switch (filter.operator) {
                                        case 'equals':
                                            return proc === filterValue;
                                        case 'greater':
                                            return proc > filterValue;
                                        case 'less':
                                            return proc < filterValue;
                                        default:
                                            return true;
                                    }
                                });
                            }
                        }
                        break;

                    case 'object_sender':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.filter(item => {
                                const sender = (item.sender || '').toLowerCase();
                                switch (filter.operator) {
                                    case 'contains':
                                        return sender.includes(searchValue);
                                    case 'not_contains':
                                        return !sender.includes(searchValue);
                                    case 'equals':
                                        return sender === searchValue;
                                    case 'not_equals':
                                        return sender !== searchValue;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'object_remarks_count':
                        if (filter.value && filter.value !== '') {
                            const filterValue = Number(filter.value);
                            if (!isNaN(filterValue)) {
                                filtered = filtered.filter(item => {
                                    const remarksCount = item.tbl ? item.tbl.length : 0;
                                    switch (filter.operator) {
                                        case 'equals':
                                            return remarksCount === filterValue;
                                        case 'greater':
                                            return remarksCount > filterValue;
                                        case 'less':
                                            return remarksCount < filterValue;
                                        default:
                                            return true;
                                    }
                                });
                            }
                        }
                        break;

                    case 'object_repeated_remarks_count':
                        if (filter.value && filter.value !== '') {
                            const filterValue = Number(filter.value);
                            if (!isNaN(filterValue)) {
                                filtered = filtered.filter(item => {
                                    const repeatedCount = item.tbl ? item.tbl.filter(remark => remark.link_to_item).length : 0;
                                    switch (filter.operator) {
                                        case 'equals':
                                            return repeatedCount === filterValue;
                                        case 'greater':
                                            return repeatedCount > filterValue;
                                        case 'less':
                                            return repeatedCount < filterValue;
                                        default:
                                            return true;
                                    }
                                });
                            }
                        }
                        break;

                    case 'object_ext_date':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.filter(item => {
                                const itemDate = parseDate(item.ext_date);
                                if (!itemDate || isNaN(itemDate.getTime())) return false;

                                switch (filter.operator) {
                                    case 'equals':
                                        return itemDate.getDate() === filterDate.getDate() &&
                                            itemDate.getMonth() === filterDate.getMonth() &&
                                            itemDate.getFullYear() === filterDate.getFullYear();
                                    case 'greater':
                                        return itemDate > filterDate;
                                    case 'less':
                                        return itemDate < filterDate;
                                    case 'between':
                                        if (!filter.value2) return true;
                                        const filterDate2 = new Date(filter.value2);
                                        return itemDate >= filterDate && itemDate <= filterDate2;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'object_deadline':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.filter(item => {
                                const itemDate = parseDate(item.deadline);
                                if (!itemDate || isNaN(itemDate.getTime())) return false;

                                switch (filter.operator) {
                                    case 'equals':
                                        return itemDate.getDate() === filterDate.getDate() &&
                                            itemDate.getMonth() === filterDate.getMonth() &&
                                            itemDate.getFullYear() === filterDate.getFullYear();
                                    case 'greater':
                                        return itemDate > filterDate;
                                    case 'less':
                                        return itemDate < filterDate;
                                    case 'between':
                                        if (!filter.value2) return true;
                                        const filterDate2 = new Date(filter.value2);
                                        return itemDate >= filterDate && itemDate <= filterDate2;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'object_status':
                        if (filter.value && filter.value.length > 0) {
                            const statusValues = filter.value.map(s => s.id);
                            filtered = filtered.filter(item => {
                                if (filter.logic === 'and') {
                                    return statusValues.every(status => item.status === status);
                                } else {
                                    return statusValues.some(status => item.status === status);
                                }
                            });
                        }
                        break;

                    // Дополнительные фильтры для замечаний
                    case 'responsible':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        if (filter.logic === 'and') {
                                            return userIds.every(userId => remark.responsible.includes(String(userId)));
                                        } else {
                                            return userIds.some(userId => remark.responsible.includes(String(userId)));
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'editor':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        if (filter.logic === 'and') {
                                            return userIds.every(userId => remark.editorVio?.id === userId);
                                        } else {
                                            return userIds.some(userId => remark.editorVio?.id === userId);
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'edited':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        const itemDate = parseDate(remark.edited);
                                        if (!itemDate || isNaN(itemDate.getTime())) return false;

                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate() &&
                                                    itemDate.getMonth() === filterDate.getMonth() &&
                                                    itemDate.getFullYear() === filterDate.getFullYear();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'creator':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        if (filter.logic === 'and') {
                                            return userIds.every(userId => remark.creatorVio?.id === userId);
                                        } else {
                                            return userIds.some(userId => remark.creatorVio?.id === userId);
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                    case 'created':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    tbl: item.tbl ? item.tbl.filter(remark => {
                                        const itemDate = parseDate(remark.created);
                                        if (!itemDate || isNaN(itemDate.getTime())) return false;

                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate() &&
                                                    itemDate.getMonth() === filterDate.getMonth() &&
                                                    itemDate.getFullYear() === filterDate.getFullYear();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    }) : []
                                }
                            }).filter(item => item.tbl && item.tbl.length > 0);
                        }
                        break;

                }
            });







        }

        // Сортировка данных для conclusions
        if (sortBy) {
            filtered = filtered.map(item => {
                // Сортируем tbl (замечания) внутри каждого item
                const sortedTbl = [...(item.tbl || [])].sort((a, b) => {
                    let aValue, bValue;

                    switch (sortBy) {
                        case 'num_remarks':
                            aValue = Number(a.num_remarks) || 0;
                            bValue = Number(b.num_remarks) || 0;
                            break;
                        case 'stage':
                            aValue = Number(a.stage) || 0;
                            bValue = Number(b.stage) || 0;
                            break;
                        case 'complexity':
                            aValue = a.complexity || '';
                            bValue = b.complexity || '';
                            break;
                        case 'task':
                            aValue = a.task || '';
                            bValue = b.task || '';
                            break;
                        case 'status_remark':
                            aValue = a.status_remark || '';
                            bValue = b.status_remark || '';
                            break;
                        case 'response_remark':
                            aValue = a.response_remark || '';
                            bValue = b.response_remark || '';
                            break;
                        case 'result':
                            aValue = a.result || '';
                            bValue = b.result || '';
                            break;
                        case 'responsible':
                            aValue = a.responsible?.map(r => r.short_fio || r.name).join(', ') || '';
                            bValue = b.responsible?.map(r => r.short_fio || r.name).join(', ') || '';
                            break;
                        case 'created':
                            aValue = new Date(a.created || 0);
                            bValue = new Date(b.created || 0);
                            break;
                        default:
                            return 0;
                    }
                    // Сравнение значений
                    let comparison = 0;
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        comparison = aValue.localeCompare(bValue);
                    } else if (aValue instanceof Date && bValue instanceof Date) {
                        comparison = aValue.getTime() - bValue.getTime();
                    } else {
                        comparison = aValue - bValue;
                    }

                    return sortDirection === 'desc' ? -comparison : comparison;
                });

                return {
                    ...item,
                    tbl: sortedTbl
                };
            });
        } else {
            // Сортировка по возрастанию id по умолчанию
            filtered.sort((a, b) => (a.id || 0) - (b.id || 0));
        }

        return filtered;
    }, [user.id]);



    // Обновляем отфильтрованные данные при изменении исходных данных или параметров
    useEffect(() => {
        const filtered = filterAndSortData(data, searchQuery, selectedFilters, advancedFilters, sortBy, sortDirection, filterMode);
        setFilteredData(filtered);
    }, [data, searchQuery, selectedFilters, advancedFilters, sortBy, sortDirection, filterMode, filterAndSortData]);


    const activeObjects = useMemo(() => {
        return objects.filter(object => data.some(item => item.id === object.id));
    }, [objects, data]);


    // Функция для подготовки данных для группировки
    const prepareGroupedData = useCallback((filteredData, selectedObjects, projectsList) => {
        // Для conclusions всегда показываем все данные, группируя по заключениям
        // Группируем по заключениям (conclusions)
        const groupedByConclusions = {};
        const sortedData = filteredData.sort((a, b) => parseDate(b.ext_date) - parseDate(a.ext_date));

        // filteredData.forEach(conclusion => {
        //     const conclusionId = conclusion.id;
        //     const conclusionName = conclusion.description || 'Неизвестное заключение';

        //     if (!groupedByConclusions[conclusionName]) {
        //         groupedByConclusions[conclusionName] = [];
        //     }

        //     // Добавляем все замечания из tbl в группу
        //     if (conclusion.tbl && Array.isArray(conclusion.tbl)) {
        //         conclusion.tbl.forEach(remark => {
        //             groupedByConclusions[conclusionName].push({
        //                 ...remark,
        //                 conclusionId: conclusionId,
        //                 conclusionName: conclusionName
        //             });
        //         });
        //     }
        // });

        // // Сортируем группы по названию заключения
        // const sortedGroups = Object.keys(groupedByConclusions);

        // Создаем плоский массив данных с информацией о группе
        const groupedData = [];
        const groupCounts = [];
        const groups = []

        sortedData.forEach(conclusion => {
            if (conclusion.tbl && Array.isArray(conclusion.tbl)) {
                groupedData.push(...conclusion.tbl.map(t => ({
                    ...t,
                    conclusionId: conclusion.id
                })))
                groupCounts.push(conclusion.tbl.length)
                groups.push({
                    ...conclusion,
                    name: conclusion.description,
                    repeated_count: conclusion.tbl.filter(t => t.link_to_item).length,
                    tbl: undefined
                })

            }
        })

        // sortedGroups.forEach((groupName, groupIndex) => {
        //     const groupItems = groupedByConclusions[groupName];
        //     groupCounts.push(groupItems.length);

        //     groupItems.forEach(item => {
        //         groupedData.push({
        //             ...item,
        //             group: groupIndex,
        //             groupName: groupName
        //         });
        //     });
        // });

        // Добавляем пустые элементы в конец для принудительного рендеринга
        const paddingItems = Array.from({ length: sortedData.length }, (_, index) => ({
            id: `padding-${index}`,
            num_remarks: `padding-${index}`,
            group: groupedData.length > 0 ? groupedData[groupedData.length - 1].group : 0,
            groupName: groupedData.length > 0 ? groupedData[groupedData.length - 1].groupName : 'Все заключения',
            isPadding: true
        }));

        return { groupCounts, groupedData: [...groupedData, ...paddingItems], groups };
    }, []);

    // Обновляем группированные данные при изменении отфильтрованных данных
    useEffect(() => {
        const grouped = prepareGroupedData(filteredData, selectedObjects, activeObjects);

        setGroupedData(grouped);
    }, [filteredData, selectedObjects, activeObjects, prepareGroupedData]);


    useEffect(() => {
        storeSettings({ selectedObjects });
    }, [selectedObjects]);

    useEffect(() => {
        storeSettings({ selectedFilters: Array.from(selectedFilters) });
    }, [selectedFilters]);

    useEffect(() => {
        storeSettings({ visibleColumns });
    }, [visibleColumns]);

    useEffect(() => {
        if (user.roles.includes('viewer')) {
            setVisibleColumns(prev => ({
                ...prev,
                'cost': false
            }));
        }
    }, [user])





    // useEffect(() => {
    //     console.log(hasRejectedRows)
    // }, [hasRejectedRows]);

    const handleEditedField = ({ groupId, rowId, field, value, isValueChanged, checklistItem, rejected }) => {
        // console.log(rowId, field, value, isValueChanged, checklistItem, rejected)


        if (isValueChanged) {
            // Записываем изменение в editedRows
            setEditedRows(prev => {
                const existingRow = prev.find(row => row.id === rowId && row.groupId === groupId);
                if (existingRow) {
                    // Если строка уже есть, обновляем поле
                    return prev.map(row =>
                        row.id === rowId && row.groupId === groupId
                            ? { ...row, changes: { ...row.changes, [field]: value } }
                            : row
                    );
                } else {
                    // Если строки нет, добавляем новую
                    return [...prev, {
                        groupId: groupId,
                        id: rowId,
                        changes: { [field]: value }
                    }];
                }
            });




        } else {
            setEditedRows(prev => {
                const existingRow = prev.find(row => row.id === rowId && row.groupId === groupId);
                if (existingRow) {
                    const changes = existingRow.changes;
                    delete changes[field];
                    if (Object.keys(changes).length === 0) {
                        return prev.filter(row => row.id !== rowId && row.groupId === groupId);
                    }
                    return prev.map(row => row.id === rowId && row.groupId === groupId ? { ...row, changes } : row);
                }
                return prev;
            });
        }



        setEditing({ id: null, field: null })
    }

    const handleChangeEdititng = ({ id, field }) => {
        setEditing({ id, field })
    }


    const handleDeleteObject = async (object) => {
        if (window.confirm(`Вы уверены, что хотите удалить объект "${object.id}"?`)) {
            try {
                const response = await $authHost.delete(`/apig/reestr/${object.id}`, {
                    data: {
                        modifier: user.id
                    }
                });
                const { success } = response.data;

                if (success) {
                    // Обновить данные - убираем удаленный объект из состояния
                    // setData(prevData => prevData.filter(item => item.id !== object.id));
                    // alert('Объект успешно удален');
                } else {
                    throw new Error(response.error || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Error deleting object:', error);
                alert(`Ошибка при удалении: ${error.message}`);
            }
        }
    };

    // Функции для работы с выбранными строками
    const handleRowSelection = (rowId, checked) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(new Set(filteredData.map(row => row.id)));
        } else {
            setSelectedRows(new Set());
        }
    };

    // Обработчик сортировки
    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            // Переключаем направление сортировки
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Устанавливаем новый столбец для сортировки
            setSortBy(columnId);
            setSortDirection('asc');
        }
    };

    // Обработчики для управления видимостью столбцов
    const handleColumnMenuOpen = (event) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnVisibilityChange = (columnId, visible) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnId]: visible
        }));
    };

    const handleShowAllColumns = () => {
        setVisibleColumns(columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}));
    };

    const handleHideAllColumns = () => {
        // Оставляем хотя бы первый столбец видимым
        const newVisibility = columns.reduce((acc, col, index) => ({
            ...acc,
            [col.id]: index === 0
        }), {});
        setVisibleColumns(newVisibility);
    };

    // Получаем только видимые столбцы
    const getVisibleColumns = useCallback(() => {
        return columns.filter(col => visibleColumns[col.id]);
    }, [visibleColumns]);

    // Функция для вычисления динамической ширины столбцов
    const getColumnWidths = useCallback(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.id]);
        if (visibleCols.length === 0) return {};

        // Получаем ширину экрана
        const screenWidth = window.innerWidth;
        const containerPadding = 32; // Отступы контейнера
        const availableWidth = screenWidth - containerPadding;

        // Вычисляем сумму минимальных ширин
        const totalMinWidth = visibleCols.reduce((sum, col) => sum + col.minWidth, 0);

        // Если доступная ширина больше суммы минимальных ширин, распределяем оставшееся место
        if (availableWidth > totalMinWidth) {
            const extraWidth = availableWidth - totalMinWidth;
            const flexibleCols = visibleCols.filter(col => !['id', 'cost'].includes(col.id)); // Не растягиваем узкие столбцы
            const extraPerCol = flexibleCols.length > 0 ? extraWidth / flexibleCols.length : 0;

            return visibleCols.reduce((acc, col) => {
                const isFlexible = flexibleCols.includes(col);
                acc[col.id] = isFlexible ? col.minWidth + extraPerCol : col.minWidth;
                return acc;
            }, {});
        }

        // Если места мало, используем минимальные ширины
        return visibleCols.reduce((acc, col) => {
            acc[col.id] = col.minWidth;
            return acc;
        }, {});
    }, [visibleColumns]);

    // Пересчитываем ширины столбцов при изменении видимости
    useEffect(() => {
        setColumnWidths(getColumnWidths());
    }, [getColumnWidths]);

    // Пересчитываем ширины при изменении размера окна
    useEffect(() => {
        const handleResize = () => {
            setColumnWidths(getColumnWidths());
        };

        window.addEventListener('resize', handleResize);
        // Начальный расчет
        setColumnWidths(getColumnWidths());

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [getColumnWidths]);



    const handleCancelChanges = () => {
        setEditedRows([]);
    };

    const handleSaveChanges = async () => {
        console.log('handleSaveChanges')
        try {
            const changes = {}
            editedRows.forEach(row => {
                const id = row.id
                const groupId = row.groupId
                if (!changes[groupId]) {
                    changes[groupId] = {}
                }
                changes[groupId][id] = row.changes
            })

            console.log('changes', changes)
            // Отправляем изменения на сервер
            // const response = await $authHost.put('/apig/reestr/batch', {
            //     updates: editedRows.map(row => ({
            //         id: row.id,
            //         changes: row.changes,
            //         modifier: user.id
            //     }))
            // });

            const response = await $authHost.post('/apig/state/conclusions', {
                user_id: user.vio_id,
                changes: changes
            }, {
                timeout: 30000
            });

            if (response.status >= 200 && response.status < 300) {
                // Очищаем editedRows после успешного сохранения
                setEditedRows([]);
                dispatch(fetchConclusions())
                // Обновляем данные с сервера
                // fetchData();
            } else {
                throw new Error(response.data.error || 'Ошибка при сохранении');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            alert(`Ошибка при сохранении: ${error.message}`);
        }
    };


    // Функции для управления продвинутыми фильтрами
    const handleApplyAdvancedFilters = () => {
        // Фильтры применяются автоматически через useEffect
        setShowAdvancedFilters(false);
    };

    // Функция-обертка для setFilterMode с сохранением в localStorage
    const handleFilterModeChange = (newMode) => {
        setFilterMode(newMode);
        storeSettings({ advancedFilters, filterMode: newMode });
    };

    const handleClearAdvancedFilters = () => {
        setAdvancedFilters([]);
        storeSettings({ advancedFilters: [], filterMode });
    };

    const getActiveAdvancedFiltersCount = () => {
        return advancedFilters.length;
    };

    const addFilter = (filterId) => {
        const filter = availableFilters.find(f => f.id === filterId);
        if (filter) {
            const newFilter = {
                id: filterId,
                label: filter.label,
                type: filter.type,
                options: filter.options,
                operator: filter.type === 'date' || filter.type === 'number' ? 'equals' :
                    filter.type === 'text' ? 'contains' : null,
                value: filter.type === 'date' || filter.type === 'number' ? null :
                    filter.type === 'boolean' ? '' :
                        filter.type === 'text' ? '' :
                            filter.type === 'multi-select' ? [] : [],
                value2: filter.type === 'date' ? null : null, // Для фильтра "между"
                logic: filter.type === 'multi-select' ? 'or' : null,
                enabled: true
            };
            const newFilters = [...advancedFilters, newFilter];
            setAdvancedFilters(newFilters);
            storeSettings({ advancedFilters: newFilters, filterMode });
        }
    };

    const removeFilter = (filterId) => {
        const newFilters = advancedFilters.filter(f => f.id !== filterId);
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const updateFilter = (filterId, updates) => {
        const newFilters = advancedFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        );
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const getAvailableFiltersForSelect = () => {
        const usedFilterIds = advancedFilters.map(f => f.id);
        return availableFilters.filter(f => !usedFilterIds.includes(f.id));
    };

    // Функция для определения текущего статуса по фильтру
    const getCurrentStatusFromFilter = () => {
        const statusFilter = advancedFilters.find(filter => filter.id === 'status');

        if (!statusFilter || !statusFilter.value || statusFilter.value.length === 0) {
            return 'Отобразить все';
        }

        // Получаем массив значений из фильтра
        const filterValues = statusFilter.value.map(v => v.name || v.id || v).sort();

        // Ищем статус с полным соответствием фильтра
        for (const [statusKey, statusData] of Object.entries(statuses)) {
            const statusFilterValues = [...statusData.filter].sort();

            // Проверяем полное соответствие массивов
            if (statusFilterValues.length === filterValues.length &&
                statusFilterValues.every((val, index) => val === filterValues[index])) {
                return statusKey;
            }
        }

        return 'Другие';
    };





    // Функции для управления меню экспорта
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    const handleExportFormatSelect = (format) => {
        handleExportMenuClose();
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };
    // Функция экспорта в Excel для ConclusionsView
    const exportToExcel = async () => {
        try {
            // Создаем новую рабочую книгу ExcelJS
            const workbook = new ExcelJS.Workbook();
            workbook.created = new Date();
            workbook.modified = new Date();
            workbook.title = 'Реестр заключений ООО "КОМОКС"';

            // Создаем лист
            const worksheet = workbook.addWorksheet('Реестр заключений');

            // Закрепляем первую строку
            worksheet.views = [
                { state: 'frozen', ySplit: 1 }
            ];

            // Определяем заголовки колонок для ConclusionsView
            const headers = [
                '№',
                'Стадия',
                'Сложность',
                'Замечания',
                'Статус замечания',
                'Ответ на замечание',
                'Результат',
                'Исполнитель',
                'Редактор',
                'Дата изменения',
                'Создатель',
                'Дата создания'
            ];

            // Добавляем заголовки
            // const headerRow = worksheet.addRow(headers);

            // // Стилизуем заголовки
            // headerRow.eachCell((cell, colNumber) => {
            //     cell.fill = {
            //         type: 'pattern',
            //         pattern: 'solid',
            //         fgColor: { argb: 'FFCD5C5C' }
            //     };
            //     cell.font = {
            //         name: 'Arial',
            //         size: 10,
            //         bold: true,
            //         color: { argb: 'FFFFFFFF' }
            //     };
            //     cell.alignment = {
            //         vertical: 'middle',
            //         horizontal: 'center',
            //         wrapText: true
            //     };
            //     cell.border = {
            //         top: { style: 'thin' },
            //         left: { style: 'thin' },
            //         bottom: { style: 'thin' },
            //         right: { style: 'thin' }
            //     };

            // });


            // Группируем данные по объектам для экспорта
            // const exportData = [];

            // Группируем данные по объектам как в react-virtuoso
            const sortedData = filteredData.sort((a, b) => parseDate(b.ext_date) - parseDate(a.ext_date));

            let allRows = []
            let objectRows = []
            let objectDataRows = []

            filteredData.forEach((object, objectIndex) => {

                // Добавляем строку проекта
                const objectRow = [object.description];
                allRows.push(objectRow);
                objectRows.push(allRows.length + 1)
                allRows.push([`Заключение: ${object.name_conclusion}`]);
                objectDataRows.push(allRows.length + 1)
                if (object.attach?.length > 0) {
                    allRows.push([`Вложения: ${object.attach.length}`]);
                    objectDataRows.push(allRows.length + 1)
                }
                allRows.push([`Готовность: ${object.proc}%`]);
                objectDataRows.push(allRows.length + 1)
                allRows.push([`Отправитель: ${object.sender}`]);
                objectDataRows.push(allRows.length + 1)
                allRows.push([`Замечания / Повторные замечания: ${object.tbl.length}/${object.tbl.filter(t => t.link_to_item).length}`]);
                objectDataRows.push(allRows.length + 1)
                allRows.push([`От даты: ${object.ext_date}`]);
                objectDataRows.push(allRows.length + 1)
                allRows.push([`Срок: ${object.deadline}`]);
                objectDataRows.push(allRows.length + 1)
                allRows.push([`Статус: ${statuses[object.status]?.stage || object.status}`]);
                objectDataRows.push(allRows.length + 1)

                const tableRows = []

                // Добавляем данные объектов проекта
                object.tbl.forEach(item => {


                    tableRows.push([
                        item.id,
                        statuses[item.stage]?.stage || item.stage,
                        complexityOptions.find(c => c.value === (item.complexity || 'II'))?.label || item.complexity,
                        item.task,
                        statusOptions.find(s => s.id === item.status_remark)?.name || item.status_remark,
                        item.response_remark,
                        item.result,
                        item?.responsibleVio?.map(r => r.short_fio || r.name).join(', ') || '',
                        item?.editorVio?.short_fio || item?.editorVio?.name || '',
                        item.edited,
                        item?.creatorVio?.short_fio || item?.creatorVio?.name || '',
                        item.created
                    ]);

                });

                allRows.push(...tableRows);
            });

            // Добавляем одну большую таблицу со всеми данными
            const endRow = allRows.length + 1; // +1 для заголовков
            worksheet.addTable({
                name: 'ObjectsReestrTable',
                ref: `A1:O${endRow}`,
                headerRow: true,
                totalsRow: false,
                style: { theme: 'TableStyleLight7', showRowStripes: true },
                columns: headers.map(h => ({ name: h, filterButton: true })),
                rows: allRows
            });
            const colWidths = [8, 12, 13, 40, 20, 40, 40, 22, 22, 20, 22, 20]
            const centerCols = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12]


            // Устанавливаем ширину столбцов
            colWidths.forEach((width, index) => {
                const column = worksheet.getColumn(index + 1);
                column.width = width;
            });

            const dateCols = [10, 12]
            dateCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = 'dd/mm/yyyy'
            })

            // const currencyCols = [13, 14]
            // currencyCols.forEach(colNumber => {
            //     const column = worksheet.getColumn(colNumber);
            //     column.numFmt = '# ##0.00'
            // })

            const numberCols = [1]
            numberCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '0'
            })

            // const textCols = [2, 3, 5, 6, 7, 9, 10, 11, 12, 14, 16, 17]
            // textCols.forEach(colNumber => {
            //     const column = worksheet.getColumn(colNumber);
            //     column.numFmt = ''
            // })

            const statusCols = [2]


            // Устанавливаем черный цвет текста для всех ячеек таблицы
            for (let rowNumber = 1; rowNumber <= endRow; rowNumber++) {
                const row = worksheet.getRow(rowNumber);
                // row.height = undefined

                row.eachCell((cell, colNumber) => {
                    let color = 'FF000000'
                    if (statusCols.includes(colNumber) && rowNumber !== 1) color = Object.values(statuses).find(s => s.stage === cell.text)?.argb || 'FF000000'
                    // if (colNumber === codeCol) color = codeColors[cell.text[0]] || 'FF000000'
                    cell.font = {
                        ...cell.font,
                        name: 'Arial',
                        size: objectRows.includes(rowNumber) || rowNumber === 1 ? 11 : 10,
                        bold: objectRows.includes(rowNumber) || rowNumber === 1 || statusCols.includes(colNumber),
                        color: { argb: color } // Черный цвет

                    };
                    cell.alignment = {
                        horizontal: ![...objectRows, ...objectDataRows].includes(rowNumber) && (centerCols.includes(colNumber) || rowNumber === 1) ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: ![...objectRows, ...objectDataRows].includes(rowNumber) || rowNumber === 1
                    }


                });
                row.height = undefined; // Это включает автоподбор

                if (typeof row.commit === 'function') row.commit();
            }


            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Реестр заключений ООО "КОМОКС" от ${dateStr}.xlsx`;

            // Экспортируем файл
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);


            console.log('Excel файл успешно создан:', fileName);
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };


    // Функция экспорта в PDF
    const exportToPDF = () => {
        try {
            // Создаем PDF документ
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            pdf.setFont("arial", "normal");
            pdf.setLanguage('ru')

            // Настройки страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 5;
            const contentWidth = pageWidth - 2 * margin;
            const contentHeight = pageHeight - 2 * margin;

            // Заголовок документа
            pdf.setFontSize(14);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(247, 150, 70);
            pdf.text('Реестр заключений ООО "КОМОКС"', pageWidth / 2, 10, { align: 'center' });

            // Информация о документе
            pdf.setFontSize(10);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(100, 100, 100);
            const currentDate = new Date().toLocaleDateString('ru-RU');
            pdf.text(`Дата экспорта: ${currentDate}`, pageWidth - margin, 10, { align: 'right' });
            pdf.text(`Всего объектов: ${filteredData.length}`, margin, 10);

            let currentY = 15;
            let pageNumber = 1;
            let totalPages = 1; // Будет обновлено после создания всех страниц

            // Определяем заголовки таблицы (как в Excel)
            const headers = [
                '№',
                'Стадия',
                'Сложность',
                'Замечания',
                'Статус замечания',
                'Ответ на замечание',
                'Результат',
                'Исполнитель',
                'Редактор',
                'Дата изменения',
                'Создатель',
                'Дата создания'
            ];

            // Группируем данные по объектам как в Excel
            const sortedData = filteredData.sort((a, b) => parseDate(b.ext_date) - parseDate(a.ext_date));

            let allRows = []
            let objectRows = []
            let objectDataRows = []

            sortedData.forEach((object, objectIndex) => {
                // Добавляем строку проекта
                const tableData = [
                    [{ content: object.description, colSpan: 12, styles: { halign: 'center', fontStyle: 'bold', fontSize: 9 } }]
                ]
                tableData.push([{ content: `Заключение: ${object.name_conclusion}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                if (object.attach?.length > 0) {
                    tableData.push([{ content: `Вложения: ${object.attach.length}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                }
                tableData.push([{ content: `Готовность: ${object.proc}%`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                tableData.push([{ content: `Отправитель: ${object.sender}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                tableData.push([{ content: `Замечания / Повторные замечания: ${object.tbl.length}/${object.tbl.filter(t => t.link_to_item).length}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                tableData.push([{ content: `От даты: ${object.ext_date}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                tableData.push([{ content: `Срок: ${object.deadline}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])
                tableData.push([{ content: `Статус: ${statuses[object.status]?.stage || object.status}`, colSpan: 12, styles: { halign: 'left', fontSize: 9 } }])


                // Добавляем данные объектов проекта
                object.tbl.forEach(item => {
                    tableData.push([
                        item.id,
                        statuses[item.stage]?.stage || item.stage,
                        complexityOptions.find(c => c.value === (item.complexity || 'II'))?.label || item.complexity,
                        item.task,
                        statusOptions.find(s => s.id === item.status_remark)?.name || item.status_remark,
                        item.response_remark,
                        item.result,
                        item?.responsibleVio?.map(r => r.short_fio || r.name).join(', ') || '',
                        item?.editorVio?.short_fio || item?.editorVio?.name || '',
                        item.edited,
                        item?.creatorVio?.short_fio || item?.creatorVio?.name || '',
                        item.created
                    ]);
                });

                // Добавляем таблицу с улучшенным форматированием
                autoTable(pdf, {
                    head: [headers],
                    body: tableData,
                    startY: currentY,
                    margin: { left: margin, right: margin },
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1,
                        lineColor: [208, 215, 224], // D0D7E0 - как в Excel
                        lineWidth: 0.1,
                        textColor: [0, 0, 0],
                        font: 'arial',
                        fontStyle: 'normal'
                    },
                    headStyles: {
                        fillColor: [247, 150, 70], // Синий заголовок как в Excel
                        textColor: [255, 255, 255],
                        font: 'arial',
                        fontStyle: 'bold',
                        fontSize: 9,
                        cellPadding: 1,
                        halign: 'center',
                        valign: 'middle'
                    },
                    alternateRowStyles: {
                        fillColor: [253, 233, 217] // E6F3FF - светло-голубой для четных строк
                    },
                    columnStyles: {
                        0: { cellWidth: 10, valign: 'middle', halign: 'center' },   // №
                        1: { cellWidth: 15, valign: 'middle', halign: 'center' },  // Стадия
                        2: { cellWidth: 14, valign: 'middle', halign: 'center' },  // Сложность
                        3: { cellWidth: 40, valign: 'middle' },  // Замечания
                        4: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Статус замечания
                        5: { cellWidth: 40, valign: 'middle' },  // Ответ на замечание
                        6: { cellWidth: 40, valign: 'middle' },  // Результат
                        7: { cellWidth: 23, valign: 'middle', halign: 'center' },  // Исполнитель
                        8: { cellWidth: 22, valign: 'middle', halign: 'center' },  // Редактор
                        9: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Дата изменения
                        10: { cellWidth: 22, valign: 'middle', halign: 'center' },  // Создатель
                        11: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Дата создания
                    },
                    didDrawPage: function (data) {
                        // Функция будет вызвана для каждой страницы, но номера добавим позже
                    }
                });

                // Получаем позицию после таблицы
                const tableEndY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : currentY + 50;
                currentY = tableEndY + 5;

                // Не добавляем разделитель между проектами (как в Excel)
            });


            // Получаем общее количество страниц
            totalPages = pdf.getNumberOfPages();

            // Добавляем номера страниц на все страницы
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                pdf.setPage(pageNum);

                // Добавляем номер страницы внизу
                pdf.setFontSize(8);
                pdf.setFont("arial", "normal");
                pdf.setTextColor(100, 100, 100);
                pdf.text(`Стр. ${pageNum} из ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
            }

            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Реестр заключений ООО "КОМОКС" от ${dateStr}.pdf`;

            // Сохраняем PDF
            pdf.save(fileName);
            console.log('PDF файл успешно создан с форматированием как в Excel');
        } catch (error) {
            console.error('Ошибка при экспорте в PDF:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };
    return (
        <Box sx={{
            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',

            p: 0,
            m: 0,
            display: 'flex',
            flex: 1,
            height: '100%',
            flexDirection: 'column',
            // overflow: 'hidden',
            width: '100%'

        }}>
            <Paper sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: 0,
                boxShadow: 'none',
                overflow: 'hidden',
                border: 'none',
                width: '100%',
                minHeight: '100%',
            }}>
                {/* Первая строка - заголовок, поиск, темная тема */}
                <Box sx={{
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    p: { xs: 0.5, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    // gap: { xs: 1, md: 1 },
                }}>
                    {!fromStartPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 1, width: '100%' }}>
                            <ViewListIcon sx={{ fontSize: { xs: 24, md: 32 }, color: darkMode ? 'white' : 'black' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.1, color: darkMode ? 'white' : 'black' }}>
                                Замечания Заказчика
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        {/* <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            border: darkMode ? '2px solid #64b5f6' : '2px solid #1976d2'
                        }}>
                            <BusinessIcon sx={{
                                fontSize: '28px',
                                color: darkMode ? '#64b5f6' : '#1976d2'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                color: darkMode ? '#f5f7fa' : '#333',
                                mb: 0.5,
                            }}>
                                Реестр объектов
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Управление объектами строительства
                            </Typography>
                        </Box> */}
                        {/* Фильтр по дереву проектов */}
                        <TextField
                            size="small"
                            placeholder={getDisplayText()}
                            value={treeSearchQuery}
                            onChange={(e) => setTreeSearchQuery(e.target.value)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setProjectsFilterAnchor(e.currentTarget);
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {treeSearchQuery && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setTreeSearchQuery('')
                                                    }}
                                                >
                                                    <ClearIcon fontSize="small" />

                                                </IconButton>
                                            )}
                                            {/* <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setProjectsFilterAnchor(e.currentTarget);
                                                }}
                                            >
                                                <KeyboardArrowDownIcon />
                                            </IconButton> */}
                                        </InputAdornment>
                                    )
                                }
                            }}
                            // InputProps={{
                            //     endAdornment: (
                            //         <IconButton
                            //             size="small"
                            //             onClick={(e) => {
                            //                 e.stopPropagation();
                            //                 setProjectsFilterAnchor(e.currentTarget);
                            //             }}
                            //         >
                            //             <KeyboardArrowDownIcon />
                            //         </IconButton>
                            //     ),
                            // }}
                            sx={{
                                minWidth: { xs: 'auto', md: '300px' },
                                '& .MuiInputBase-root': {
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                },
                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2'
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                    opacity: 1
                                },
                                '& .MuiIconButton-root': {
                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                }
                            }}
                        />

                        {/* Меню с деревом проектов */}
                        <Popper
                            open={Boolean(projectsFilterAnchor)}
                            anchorEl={projectsFilterAnchor}
                            placement="bottom-start"
                            style={{
                                zIndex: 1300,
                                width: projectsFilterAnchor ? Math.max(projectsFilterAnchor.clientWidth, 350) : 350
                            }}
                        >
                            <Paper
                                ref={projectsFilterPopperRef}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    maxHeight: '600px',
                                    width: '400px',
                                    overflow: 'auto',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    mt: 0.5
                                }}
                            >
                                {/* Заголовок с чипами */}
                                <Box sx={{ px: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {/* <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        Фильтр по проектам
                                    </Typography> */}
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                            label="Отметить все"
                                            size="small"
                                            onClick={selectAllFilters}
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '24px',
                                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                                color: darkMode ? '#64b5f6' : '#1976d2',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'
                                                }
                                            }}
                                        />
                                        <Chip
                                            label="Снять все"
                                            size="small"
                                            onClick={clearAllFilters}
                                            sx={{
                                                fontSize: '0.75rem',
                                                height: '24px',
                                                bgcolor: darkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
                                                color: darkMode ? '#f44336' : '#d32f2f',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'
                                                }
                                            }}
                                        />

                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => setProjectsFilterAnchor(null)}
                                        sx={{
                                            ml: 1,
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                            '&:hover': {
                                                backgroundColor: darkMode ? '#555' : '#e0e0e0'
                                            }
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>

                                {/* SimpleTreeView с чекбоксами */}
                                <Box sx={{ p: 1 }}>
                                    {treeData.length > 0 ? (
                                        <SimpleTreeView
                                            sx={{
                                                '& .MuiTreeItem-root': {
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                },
                                                '& .MuiTreeItem-content': {
                                                    py: '0',
                                                    gap: 0,
                                                    borderRadius: '4px',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                    },
                                                    '&.Mui-selected': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
                                                    }
                                                },
                                                '& .MuiTreeItem-group': {
                                                    // marginLeft: '16px',
                                                    borderLeft: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                    // paddingLeft: '8px'
                                                },
                                                '& .MuiTreeItem-iconContainer': {
                                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                                    // marginRight: '4px'
                                                }
                                            }}
                                        >
                                            {treeData.map(item => renderTreeItem(item))}
                                        </SimpleTreeView>
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2
                                            }}>
                                                Нет данных о проектах
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Popper>
                    </Box>

                    {/* Центральная часть - поиск и KDP селект */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        maxWidth: { xs: '100%', md: '600px' },
                        minWidth: { xs: '100%', sm: '400px', md: '500px' },
                        order: { xs: 3, md: 0 }
                    }}>


                        {/* Поиск */}
                        <Box sx={{
                            flex: 1,
                            position: 'relative'
                        }}>
                            <TextField
                                size="small"
                                placeholder="Поиск..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <SearchIcon sx={{
                                            mr: 1,
                                            color: darkMode ? '#bbb' : 'text.secondary',
                                            fontSize: { xs: '16px', md: '20px' }
                                        }} />,
                                        endAdornment: searchQuery && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchQuery('')}
                                                sx={{
                                                    color: darkMode ? '#bbb' : '#666',
                                                    '&:hover': {
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f8fafc',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&::placeholder': {
                                            color: darkMode ? '#bbb' : '#666',
                                            opacity: 1
                                        }
                                    }
                                }}
                            />
                            {/* Индикатор результатов поиска */}
                            {/* {searchQuery && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        mt: 0.5,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Найдено: {filteredData.length} из {data.length}
                                </Typography>
                            )} */}
                        </Box>
                        {/* Правая часть - кнопки управления */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* Кнопка настроек столбцов */}
                            <Tooltip title="Настроить видимость столбцов">
                                <IconButton
                                    onClick={handleColumnMenuOpen}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#666',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ViewColumnIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                                </IconButton>
                            </Tooltip>

                            {/* Кнопка темной темы */}
                            {/* <IconButton
                                onClick={() => dispatch(toggleDarkMode())}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#666',
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {darkMode ? <LightModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> : <DarkModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />}
                            </IconButton> */}
                            {!fromStartPage && (
                                <IconButton
                                    onClick={onClose}
                                    size="small"
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        p: { xs: 0.5, md: 1 },
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>


                </Box>

                {/* Вторая строка - панель инструментов */}
                <Box sx={{
                    p: { xs: 1, md: 1 },
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    bgcolor: darkMode ? '#252525' : '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, md: 2 },
                }}>
                    {/* Индикатор активных фильтров */}
                    {/* {(searchQuery || filterMode !== 'all' || sortMode !== 'created_desc' || selectedKdp.length > 0) && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            p: 1,
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            borderRadius: 1,
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                        }}>
                            <Typography variant="caption" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 600 }}>
                                Активные фильтры:
                            </Typography>
                            {searchQuery && (
                                <Chip
                                    label={`Поиск: "${searchQuery}"`}
                                    size="small"
                                    onDelete={() => setSearchQuery('')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {selectedKdp.length > 0 && (
                                <Chip
                                    label={`KDP: ${selectedKdp.length} выбрано`}
                                    size="small"
                                    onDelete={() => setSelectedKdp([])}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {filterMode !== 'all' && (
                                <Chip
                                    label={`Фильтр: ${filterMode === 'my' ? 'Мои объекты' : 'Активные этапы'}`}
                                    size="small"
                                    onDelete={() => setFilterMode('all')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {sortMode !== 'created_desc' && (
                                <Chip
                                    label={`Сортировка: ${sortMode}`}
                                    size="small"
                                    onDelete={() => setSortMode('created_desc')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                        color: darkMode ? '#81c784' : '#388e3c',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                        </Box>
                    )} */}

                    {/* Основные элементы управления */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 1, md: 2 },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between'
                    }}>
                        {/* Левая группа - кнопка создания и фильтры */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flex: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: 'wrap',
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>
                            {/* Кнопка создания */}
                            {/* {user.roles?.includes('project_manager') && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenCreateDialog()}
                                    sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#388e3c',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Создать объект
                                </Button>
                            )} */}

                            {/* Кнопка продвинутых фильтров */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                sx={{
                                    borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                    ...(getActiveAdvancedFiltersCount() > 0 && {
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                    })
                                }}
                            >
                                Фильтры
                                {getActiveAdvancedFiltersCount() > 0 && (
                                    <Chip
                                        label={getActiveAdvancedFiltersCount()}
                                        size="small"
                                        sx={{
                                            ml: 0.5,
                                            bgcolor: darkMode ? '#1976d2' : '#1976d2',
                                            color: '#fff',
                                            fontSize: '0.7rem',
                                            height: '16px',
                                            minWidth: '16px'
                                        }}
                                    />
                                )}
                            </Button>
                            {/* Объединенная кнопка экспорта с меню */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={handleExportMenuOpen}
                                disabled={filteredData.length === 0}
                                sx={{
                                    borderColor: darkMode ? '#1976d2' : '#1976d2',
                                    color: darkMode ? '#1976d2' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#1565c0' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:disabled': {
                                        borderColor: darkMode ? '#666' : '#ccc',
                                        color: darkMode ? '#666' : '#ccc',
                                        transform: 'none',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Экспорт
                            </Button>
                            {/* Меню выбора формата экспорта */}
                            <Menu
                                anchorEl={exportMenuAnchor}
                                open={Boolean(exportMenuAnchor)}
                                onClose={handleExportMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                sx={{
                                    '& .MuiPaper-root': {
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                        boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('excel')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                        }
                                    }}
                                >
                                    <FileDownloadIcon sx={{ mr: 1, color: '#4caf50' }} />
                                    Экспорт в Excel
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('pdf')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                        }
                                    }}
                                >
                                    <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                                    Экспорт в PDF
                                </MenuItem>
                            </Menu>
                            {/* Фильтр */}
                            {/* <FormControl
                                size="small"
                                sx={{
                                    minWidth: { xs: 'auto', md: '150px' },
                                    flex: { xs: 1, sm: 'none' },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        }
                                    }
                                }}
                            >
                                <InputLabel>Фильтр</InputLabel>
                                <Select
                                    value={filterMode}
                                    size='small'
                                    onChange={(e) => setFilterMode(e.target.value)}
                                    label="Фильтр"
                                    startAdornment={<FilterIcon sx={{
                                        mr: 1,
                                        color: darkMode ? '#bbb' : 'text.secondary',
                                        fontSize: '18px'
                                    }} />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.75rem',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Все объекты ({data.length})</MenuItem>
                                   
                                </Select>
                            </FormControl> */}

                            {/* Кнопка сброса фильтров */}
                            {/* {(searchQuery || filterMode !== 'all' || selectedKdp.length > 0) && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setFilterMode('all');
                                        setSelectedKdp([]);
                                    }}
                                    sx={{
                                        mb: 1,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                        }
                                    }}
                                >
                                    Сбросить фильтры
                                </Button>
                            )} */}
                        </Box>

                        {/* Правая группа - кнопка редактирования */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between'
                        }}>
                            {/* Индикатор режима редактирования */}
                            {/* {editMode && (
                                <Chip
                                    label="Режим редактирования"
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                    }}
                                />
                            )} */}

                            {/* Индикатор изменений */}
                            {/* {editedRows.length > 0 && (
                                <Chip
                                    label={`${editedRows.length} изменений`}
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                                    }}
                                />
                            )} */}

                            {/* Кнопки отмены и сохранения */}
                            {editedRows.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: { xs: 1, md: 2 },
                                    alignItems: { xs: 'stretch', md: 'center' },
                                    justifyContent: 'center'
                                }}>
                                    {/* Поле для ввода причины отклонения */}
                                    {hasRejectedRows.length > 0 && (
                                        <TextField
                                            size="small"
                                            multiline
                                            rows={3}
                                            placeholder="Причина отклонения..."
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            sx={{
                                                minWidth: { xs: '200px', md: '250px' },
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                    color: darkMode ? '#ffffff' : '#333',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#9c27b0' : '#6a1b9a',
                                                        borderWidth: '2px'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#ab47bc' : '#7b1fa2',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#ce93d8' : '#8e24aa',
                                                        borderWidth: '2px'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : '#333',
                                                    fontSize: '0.75rem',
                                                    '&::placeholder': {
                                                        color: darkMode ? '#bbb' : '#666',
                                                        opacity: 1
                                                    }
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: darkMode ? '#9c27b0' : '#6a1b9a',
                                                    fontSize: '0.75rem',
                                                    '&.Mui-focused': {
                                                        color: darkMode ? '#ce93d8' : '#8e24aa'
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={handleCancelChanges}
                                        sx={{
                                            borderColor: darkMode ? '#f44336' : '#d32f2f',
                                            color: darkMode ? '#f44336' : '#d32f2f',
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                                borderColor: darkMode ? '#f44336' : '#d32f2f',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Отменить
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveChanges}
                                        disabled={hasRejectedRows.length > 0 && !rejectionReason.trim()}
                                        sx={{
                                            bgcolor: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                ? (darkMode ? '#666' : '#ccc')
                                                : '#4caf50',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            boxShadow: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                ? 'none'
                                                : '0 2px 4px rgba(76, 175, 80, 0.3)',
                                            '&:hover': {
                                                bgcolor: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? (darkMode ? '#666' : '#ccc')
                                                    : '#388e3c',
                                                transform: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? 'none'
                                                    : 'translateY(-1px)',
                                                boxShadow: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? 'none'
                                                    : '0 4px 8px rgba(76, 175, 80, 0.4)'
                                            },
                                            '&:disabled': {
                                                bgcolor: darkMode ? '#666' : '#ccc',
                                                color: darkMode ? '#bbb' : '#999',
                                                cursor: 'not-allowed'
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Сохранить
                                    </Button>
                                </Box>
                            )}

                            {/* Кнопка редактирования */}
                            {user.roles?.includes('project_manager') && (
                                <Button
                                    variant={editMode ? "contained" : "outlined"}
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                        if (editMode && editedRows.length > 0) {
                                            if (window.confirm('У вас есть несохраненные изменения. Выйти из режима редактирования?')) {
                                                setEditMode(false);
                                                setSelectedRows(new Set());
                                                // setEditedRows([]);
                                                handleCancelChanges()
                                            }
                                        } else {
                                            setEditMode(!editMode);
                                            if (!editMode) {
                                                // Входим в режим редактирования
                                                setSelectedRows(new Set());
                                            } else {
                                                // Выходим из режима редактирования
                                                setSelectedRows(new Set());
                                            }
                                        }
                                    }}
                                    sx={{
                                        bgcolor: editMode ? '#1976d2' : 'transparent',
                                        color: editMode ? 'white' : (darkMode ? '#64b5f6' : '#1976d2'),
                                        borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: editMode ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                                        '&:hover': {
                                            bgcolor: editMode ? '#1565c0' : (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'),
                                            transform: editMode ? 'translateY(-1px)' : 'none',
                                            boxShadow: editMode ? '0 4px 12px rgba(25, 118, 210, 0.4)' : 'none'
                                        },
                                        transition: 'all 0.2s ease',
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    {editMode ? 'Завершить редактирование' : 'Редактировать'}
                                </Button>
                            )}

                            {/* Кнопка удаления выбранных строк */}
                            {/* {editMode && selectedRows.size > 0 && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleBulkDelete}
                                    sx={{
                                        bgcolor: '#f44336',
                                        color: 'white',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#d32f2f',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)'
                                        },
                                        transition: 'all 0.2s ease',
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    Удалить ({selectedRows.size})
                                </Button>
                            )} */}
                        </Box>
                    </Box>
                </Box>

                {/* Продвинутые фильтры */}
                {showAdvancedFilters && (
                    <AdvancedFiltersComponent
                        darkMode={darkMode}
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                        onApplyFilters={handleApplyAdvancedFilters}
                        onClearFilters={handleClearAdvancedFilters}
                        addFilter={addFilter}
                        removeFilter={removeFilter}
                        updateFilter={updateFilter}
                        getAvailableFiltersForSelect={getAvailableFiltersForSelect}
                        filterMode={filterMode}
                        handleFilterModeChange={handleFilterModeChange}
                    />
                )}

                {/* Таблица */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    width: '100%',
                    minWidth: '100%',
                    // overflowX: 'auto',
                }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f5f7fa' : '#333',
                            gap: 2,
                        }}>
                            <CircularProgress size={40} sx={{ color: darkMode ? '#64b5f6' : '#1976d2' }} />
                            <Typography variant="h6">Загрузка данных...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f44336' : '#d32f2f',
                        }}>
                            <Typography variant="h6">Ошибка загрузки: {error}</Typography>
                        </Box>
                    ) : (filteredData.length === 0 && groupedData.groupedData.length === 0) ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#bbb' : '#666',
                        }}>
                            <Typography variant="h6">
                                {searchQuery || advancedFilters.length > 0
                                    ? 'По вашему запросу ничего не найдено'
                                    : 'Нет данных для отображения'
                                }
                            </Typography>
                        </Box>
                    ) : (
                        <GroupedTableVirtuoso
                            key={'grouped-table'}
                            data={groupedData.groupedData}
                            overscan={900}
                            groupCounts={groupedData.groupCounts}
                            totalCount={groupedData.groupedData.length}
                            components={VirtuosoTableComponents}
                            fixedHeaderContent={() => fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths)}
                            groupContent={(index) => groupContent(index, groupedData, darkMode, user, visibleColumns, setImageViewerActiveIndex, setImageViewerVisible, setImages, setEditor)}
                            itemContent={(index, groupIndex) => {
                                const row = groupedData.groupedData[index];
                                if (!row) {
                                    console.warn('Missing row at index:', index);
                                    return null;
                                }

                                // Для conclusions данные уже находятся в row, не нужно обращаться к row.accounting
                                const cells = rowContent(index, row, {
                                    editMode,
                                    editing,
                                    saveEditedField: handleEditedField,
                                    setEditingField: handleChangeEdititng,
                                    darkMode,
                                    editedFields: editedRows.find(v => v.id === row.id)?.changes || {},
                                    selectedRows,
                                    handleRowSelection,
                                    statusOptions,
                                    canEditReady,
                                    canEditSuccess,
                                    visibleColumns,
                                    users
                                });

                                // Фильтруем ячейки по видимости столбцов
                                if (row.isPadding) {
                                    return cells; // Для padding элементов уже есть фильтрация внутри rowContent
                                }

                                // Проверяем, что cells существует и имеет правильную структуру
                                if (!cells) {
                                    return null; // Возвращаем null если cells не существует
                                }

                                if (!cells.props || !cells.props.children) {
                                    return cells; // Возвращаем как есть если структура неправильная
                                }

                                const cellsArray = React.Children.toArray(cells.props.children);
                                const visibleCells = cellsArray.filter((cell, cellIndex) => {
                                    return cellIndex < columns.length && visibleColumns[columns[cellIndex].id];
                                });

                                return React.cloneElement(cells, {}, visibleCells);
                            }}
                            style={{
                                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                width: '100%',
                                height: '100%',
                                minHeight: '400px',
                            }}

                        />

                    )}
                </Box>
            </Paper >

            {/* <ObjectDialog
                open={objectDialogOpen}
                currentUser={user}
                onClose={handleCloseObjectDialog}
                objectData={selectedObject}
                darkMode={darkMode}
                responsible={responsible}
                equipment={equipment}
                stages={stages}
                projects={projects}
                selectedProjectId={selectedProjectId}
            /> */}
            {/* <AuditHistoryDialog
                open={historyDialogOpen}
                onClose={handleCloseHistoryDialog}
                tableName="objects_reestr"
                recordId={selectedObject?.id}
                recordTitle={selectedObject?.name}
                darkMode={darkMode}
                dataMap={
                    {
                        'responsible': { 'users': { key: 'id', value: 'name' } },
                        'coexecutors': { 'users': { key: 'id', value: 'name' } },
                        'equipment': { 'equipment': { key: 'id', value: 'name' } },
                        'project_id': { 'projects': { key: 'id', value: 'name' } }
                    }
                }
                data={
                    {
                        users: responsible,
                        equipment: equipment,
                        stages: stages,
                        projects: projects,
                    }
                }
                translates={
                    {
                        'fid': '№',
                        'responsible': 'Ответственный',
                        'coexecutors': 'Соисполнители',
                        'equipment': 'Оснащение',
                        'stages': 'Этапы',
                        'project_id': 'Проект',
                        'plan_start': 'Плановое начало',
                        'plan_end': 'Плановое завершение',
                        'fact_start': 'Фактическое начало',
                        'fact_end': 'Фактическое завершение',
                    }
                }
                formatting={
                    {
                        'plan_start': formatDateShort,
                        'plan_end': formatDateShort,
                        'fact_start': formatDateShort,
                        'fact_end': formatDateShort,
                    }
                }
                ignore={
                    ['modifier']
                }
            /> */}

            {/* Меню настройки видимости столбцов */}
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        minWidth: 250,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            color: darkMode ? '#f5f7fa' : '#333',
                            padding: '0',
                            pr: 1,
                            // margin: '2px 8px',
                            borderRadius: '6px',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            },
                            // '&:first-of-type': {
                            //     marginTop: '8px'
                            // },
                            // '&:last-of-type': {
                            //     marginBottom: '8px'
                            // }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Заголовок меню */}
                <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', margin: '0 !important', padding: '0 !important', borderRadius: '0 !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? '#f5f7fa' : '#333' }}>
                        Видимость столбцов
                    </Typography>
                </MenuItem>

                <Divider sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    margin: '4px 0'
                }} />

                {/* Кнопки для показа/скрытия всех столбцов */}
                {/* <MenuItem onClick={handleShowAllColumns}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#4caf50' : '#2e7d32' }}>
                        Показать все столбцы
                    </Typography>
                </MenuItem>
                
                <MenuItem onClick={handleHideAllColumns}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#f44336' : '#d32f2f' }}>
                        Скрыть все столбцы (кроме первого)
                    </Typography>
                </MenuItem> */}

                {/* <Divider sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} /> */}

                {/* Список столбцов с чекбоксами */}
                {columns.map((column, index) => (
                    <MenuItem
                        key={column.id}
                        onClick={(e) => e.stopPropagation()}
                        disabled={column.id === 'cost' && user.roles.includes('viewer')}
                    >
                        <Checkbox
                            checked={visibleColumns[column.id] || false}
                            onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                            size="small"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                mr: 0
                            }}
                        />
                        <ListItemText
                            primary={column.label}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                color: darkMode ? '#f5f7fa' : '#333'
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>

            {/* Image Viewer */}
            <Viewer
                visible={imageViewerVisible}
                onClose={() => setImageViewerVisible(false)}
                images={images}
                activeIndex={imageViewerActiveIndex}
                zIndex={9999}
                downloadable={true}
                scalable={true}
                rotatable={true}
                zoomable={true}
                drag={true}
            />

            {editor && (
                createPortal(
                    <OnlyOfficeEditor
                        doc={{ ...editor, id: `${new Date().getTime()}` }}
                        onClose={() => setEditor(null)}
                    />,
                    document.body
                )
            )}
        </Box >
    );
};

export default ConclusionsView;
